// ContractorDashboard.js v3.0 - Visual redesign: theme system, consolidated nav, mobile bottom tabs
// Submits production logs, equipment checks, time entries, street sheets to Google Sheets
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { LogOut, Briefcase, DollarSign, Settings, ChevronLeft, ChevronRight, CheckCircle, Activity, Truck, AlertTriangle, Shield, ShieldAlert, MapPin, Shovel, Users, Loader, X, Map, ClipboardCheck, Download, Sun, Moon, FileText, Plus, Minus, MoreHorizontal, Home, Menu, Filter, Square } from 'lucide-react';
import { loadFullProject, getContractorSegments, loadLineItems, loadAssignments, getContractorAssignments, acceptAssignment, declineAssignment, assignToSub, updateSegmentField, updateDeviceField, bulkUpdateSection, createSubAssignment, loadDevices, detectContractorPhases, getPhaseStatusField, filterDevicesByPhase, filterSegmentsByPhase } from '../services/mapService';
import { isGpsEnabled, setGpsEnabled } from '../services/gpsService';
import AssignmentManager from '../components/Workflow/AssignmentManager';
import ComplianceHub from '../components/Workflow/ComplianceHub';
import { fetchWithRedirect } from '../utils/fetchHelpers';
import NotificationBell from '../components/NotificationBell';
import { getTheme, getCardStyle, getInputStyle, getButtonStyle, useMobile } from '../utils/theme';
import { GoogleMap, useJsApiLoader, Polyline, Marker, DrawingManager } from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_LIBS, STATUS_COLORS } from '../config/mapConfig';
import MapActionBar, { MapStatsBar, MapFloatingButton, getMapStatusColor, QuickStatusButtons } from '../components/Map/MapActionBar';
import MapNotesLayer from '../components/Map/MapNotesLayer';
import MyLocationMarker, { GpsToggleButton } from '../components/Map/MyLocationMarker';
import { loadMapNotes } from '../services/mapService';

// API URLs
// eslint-disable-next-line no-unused-vars
const PORTAL_URL = 'https://script.google.com/macros/s/AKfycbyUHklFqQCDIFzHKVq488fYtAIW1lChNnWV2FWHnvGEr7Eq0oREhDE5CueoBJ6k-xhKOg/exec';
const GATEWAY_URL = 'https://script.google.com/macros/s/AKfycbyFWHLgFOglJ75Y6AGnyme0P00OjFgE_-qrDN9m0spn4HCgcyBpjvMopsB1_l9MDjIctQ/exec';
const GATEWAY_SECRET = 'LYTcomm2026ClaudeGatewaySecretKey99';
const OPERATIONS_SHEET_ID = '1VciM5TqHC5neB7JzpcFkX0qyoyzjBvIS0fKkOXQqnrc';

// Submit data to Google Sheet
const submitToSheet = async (sheetName, rowData) => {
  try {
    const text = await fetchWithRedirect(GATEWAY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify({
        secret: GATEWAY_SECRET,
        action: 'sheetsAppend',
        params: {
          spreadsheetId: OPERATIONS_SHEET_ID,
          range: `${sheetName}!A:Z`,
          values: [rowData]
        }
      })
    });
    const result = JSON.parse(text);
    return result.success;
  } catch (err) {
    console.error('Submit error:', err);
    return false;
  }
};

// Skeleton loading placeholder
const Skeleton = ({ width, height, style }) => (
  <div style={{
    width: width || '100%', height: height || '20px', borderRadius: '6px',
    background: 'linear-gradient(90deg, rgba(128,128,128,0.1) 25%, rgba(128,128,128,0.2) 50%, rgba(128,128,128,0.1) 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
    ...style,
  }} />
);

const ContractorDashboard = ({ setCurrentPage, loggedInUser, setLoggedInUser, darkMode, setDarkMode }) => {
  // Theme system
  const theme = getTheme(darkMode);
  const { colors: tc } = theme;
  const isMobile = useMobile();
  const tCard = getCardStyle(darkMode);
  const tInput = getInputStyle(darkMode);

  // Tab state with persistence + browser back support
  const [activeTab, setActiveTabRaw] = useState(() => {
    try { return sessionStorage.getItem('contractor-tab') || 'dashboard'; } catch { return 'dashboard'; }
  });
  const setActiveTab = useCallback((tab) => {
    setActiveTabRaw(prev => {
      if (prev === tab) return prev;
      try { sessionStorage.setItem('contractor-tab', tab); } catch { /* noop */ }
      // Preserve App.js page state so back button doesn't break site routing
      const currentState = window.history.state || {};
      window.history.pushState({ ...currentState, contractorTab: tab }, '');
      return tab;
    });
  }, []);

  // Handle browser back/forward within dashboard
  useEffect(() => {
    const onPopState = (e) => {
      if (e.state && e.state.contractorTab && e.state.page === 'contractor-dashboard') {
        setActiveTabRaw(e.state.contractorTab);
        try { sessionStorage.setItem('contractor-tab', e.state.contractorTab); } catch { /* noop */ }
      }
    };
    window.addEventListener('popstate', onPopState);
    // Seed initial history entry with current tab
    if (!window.history.state?.contractorTab) {
      window.history.replaceState({ ...window.history.state, contractorTab: activeTab }, '');
    }
    return () => window.removeEventListener('popstate', onPopState);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ show: false, success: false, message: '' });
  const [showVersion, setShowVersion] = useState(false);
  const [gpsOn, setGpsOn] = useState(isGpsEnabled);

  // Mobile bottom tab "More" sheet
  const [moreSheetOpen, setMoreSheetOpen] = useState(false);

  // Project Map segment data
  const [mySegments, setMySegments] = useState([]);
  const [segmentsLoading, setSegmentsLoading] = useState(true);

  // Line items for contractor billing view
  const [myLineItems, setMyLineItems] = useState([]);
  const [lineItemsLoading, setLineItemsLoading] = useState(false);

  // Sub-contractor team
  const [mySubs, setMySubs] = useState([]);
  const [subsLoading, setSubsLoading] = useState(false);
  const subsLoadedRef = React.useRef(false);

  // Assignments
  const [myAssignments, setMyAssignments] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);

  // Add sub-contractor form
  const [showAddSub, setShowAddSub] = useState(false);
  const [newSub, setNewSub] = useState({ name: '', email: '', company: '' });
  const [addSubLoading, setAddSubLoading] = useState(false);
  const [addSubStatus, setAddSubStatus] = useState({ show: false, success: false, message: '' });

  // Compliance data
  const [complianceItems, setComplianceItems] = useState(null);
  const [complianceLoading, setComplianceLoading] = useState(false);

  // Invoice data
  const [myInvoices, setMyInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  // Focus state for input rings
  const [focusedInput, setFocusedInput] = useState(null);

  // Hover state for cards
  const [hoveredCard, setHoveredCard] = useState(null);

  // Street sheet daily banner
  const todayKey = `streetsheet-${new Date().toISOString().slice(0, 10)}`;
  const [streetSheetSubmittedToday, setStreetSheetSubmittedToday] = useState(() => {
    try { return localStorage.getItem(todayKey) === 'true'; } catch { return false; }
  });

  // Production map state
  const { isLoaded: mapsLoaded, loadError: mapsLoadError } = useJsApiLoader({ googleMapsApiKey: GOOGLE_MAPS_API_KEY, libraries: GOOGLE_MAPS_LIBS });
  // selectedProdSeg removed — unified selection via selectedSegIds
  const [statusUpdating, setStatusUpdating] = useState(false);
  const prodMapRef = useRef(null);
  const priorDayPopulatedRef = useRef(false);

  // Devices for organize mode
  const [myDevices, setMyDevices] = useState([]);

  // Phase detection: which work phases this contractor is assigned to
  const [myPhases, setMyPhases] = useState(['boring']);

  // Section-first navigation: null = section list, string = that section's map
  const [selectedSection, setSelectedSection] = useState(null);
  const [mapMenuOpen, setMapMenuOpen] = useState(false);

  // Unified selection (no mode toggle — tap always selects)
  const [selectedSegIds, setSelectedSegIds] = useState(new Set());
  const [selectedDevIds, setSelectedDevIds] = useState(new Set());
  const [sectionInput, setSectionInput] = useState('');
  const [assigningSection, setAssigningSection] = useState('');
  const [showSectionAssign, setShowSectionAssign] = useState(false);

  // Box select state
  const [boxSelect, setBoxSelect] = useState(null); // { startX, startY, endX, endY }
  const [boxMode, setBoxMode] = useState(false); // mobile box select toggle
  const [drawRectMode, setDrawRectMode] = useState(false);
  const [quickStatusOn, setQuickStatusOn] = useState(() => { try { const v = localStorage.getItem('lyt-quick-status'); return v === null ? true : v === 'true'; } catch { return true; } });
  const boxRef = useRef(null);

  // Visibility toggles
  const [hiddenStatuses, setHiddenStatuses] = useState(new Set());
  const [hiddenDeviceTypes, setHiddenDeviceTypes] = useState(new Set());

  // Filter panel open state (desktop)
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);

  // Map view mode: 'all' | 'lyt-only' | 'all-subs' | sub email string
  const [mapViewMode, setMapViewMode] = useState('all');

  // Map notes state
  const [mapNotes, setMapNotes] = useState([]);
  const [showNotes, setShowNotes] = useState(true);
  const [isAddingNote, setIsAddingNote] = useState(false);

  // Team tab sub-assignment
  const [assignSubOpen, setAssignSubOpen] = useState(null);
  const [selectedSubSections, setSelectedSubSections] = useState(new Set());
  const [assigningToSub, setAssigningToSub] = useState(false);

  // Production log form state (legacy — kept for street sheet/other refs)
  const [productionForm, setProductionForm] = useState({
    project: '',
    fiberFootage: '',
    splices: '',
    drops: '',
    poles: '',
    hddFootage: '',
    notes: ''
  });

  // Equipment check form state
  const [equipmentForm, setEquipmentForm] = useState({
    vehicleId: '',
    mileage: '',
    issues: '',
    safetyItems: []
  });

  // Incident form state
  const [incidentForm, setIncidentForm] = useState({
    type: '',
    project: '',
    description: '',
    injuries: false
  });

  // Street sheet form state
  const [streetSheetForm, setStreetSheetForm] = useState({
    county: '',
    project: '',
    date: new Date().toISOString().slice(0, 10),
    crews: [{ leader: '', street: '', cabinet: '', boreInfo: '' }],
    priorDay: [{ leader: '', activity: 'Bore', qty: '', cabinet: '', street: '', completed: false }],
  });

  const showNotification = (success, message) => {
    setSubmitStatus({ show: true, success, message });
    setTimeout(() => setSubmitStatus({ show: false, success: false, message: '' }), 3000);
  };

  const handleProductionSubmit = async () => {
    if (!productionForm.project) {
      showNotification(false, 'Please select a project');
      return;
    }
    setLoading(true);
    const success = await submitToSheet('Contractor Production', [
      new Date().toISOString(),
      loggedInUser?.email || '',
      loggedInUser?.name || '',
      productionForm.project,
      productionForm.fiberFootage || '0',
      productionForm.splices || '0',
      productionForm.drops || '0',
      productionForm.poles || '0',
      productionForm.hddFootage || '0',
      productionForm.notes,
      'Submitted'
    ]);
    setLoading(false);
    if (success) {
      showNotification(true, 'Production log submitted successfully!');
      setProductionForm({ project: '', fiberFootage: '', splices: '', drops: '', poles: '', hddFootage: '', notes: '' });
    } else {
      showNotification(false, 'Failed to submit. Please try again.');
    }
  };

  const handleEquipmentSubmit = async () => {
    if (!equipmentForm.vehicleId) {
      showNotification(false, 'Please enter vehicle ID');
      return;
    }
    setLoading(true);
    const success = await submitToSheet('Contractor Equipment', [
      new Date().toISOString(),
      loggedInUser?.email || '',
      loggedInUser?.name || '',
      equipmentForm.vehicleId,
      equipmentForm.mileage || '',
      equipmentForm.safetyItems.join(', '),
      equipmentForm.issues || 'None',
      'Submitted'
    ]);
    setLoading(false);
    if (success) {
      showNotification(true, 'Equipment check submitted!');
      setEquipmentForm({ vehicleId: '', mileage: '', issues: '', safetyItems: [] });
    } else {
      showNotification(false, 'Failed to submit. Please try again.');
    }
  };

  const handleIncidentSubmit = async () => {
    if (!incidentForm.type || !incidentForm.description) {
      showNotification(false, 'Please fill in type and description');
      return;
    }
    setLoading(true);
    const success = await submitToSheet('Contractor Incidents', [
      new Date().toISOString(),
      loggedInUser?.email || '',
      loggedInUser?.name || '',
      incidentForm.type,
      incidentForm.project,
      incidentForm.description,
      incidentForm.injuries ? 'Yes' : 'No',
      'Pending Review'
    ]);
    setLoading(false);
    if (success) {
      showNotification(true, 'Incident report submitted! Supervisor notified.');
      setIncidentForm({ type: '', project: '', description: '', injuries: false });
    } else {
      showNotification(false, 'Failed to submit. Please try again.');
    }
  };

  const handleLogout = () => {
    setLoggedInUser(null);
    setCurrentPage('portal-login');
  };

  // Theme-based form styles with focus ring support
  const formInputStyle = (name) => ({
    ...tInput,
    transition: 'border-color 0.2s, box-shadow 0.2s',
    boxShadow: focusedInput === name ? `0 0 0 2px ${tc.accentPrimary}40` : 'none',
    borderColor: focusedInput === name ? tc.accentPrimary : tc.border,
  });
  const inputFocusProps = (name) => ({
    onFocus: () => setFocusedInput(name),
    onBlur: () => setFocusedInput(null),
  });
  const formSelectStyle = (name) => ({ ...formInputStyle(name), cursor: 'pointer' });

  const pendingCount = myAssignments.filter(a => a.status === 'Pending').length;

  // Consolidated nav: 16 → 9 items
  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'assignments', label: 'Assignments', icon: ClipboardCheck, badge: pendingCount },
    { id: 'billing', label: 'Billing', icon: DollarSign },
    { id: 'rates', label: 'Rates', icon: FileText },
    { id: 'street-sheet', label: 'Street Sheet', icon: Shovel },
    { id: 'production', label: 'Production', icon: Activity },
    { id: 'compliance', label: 'Compliance', icon: Shield },
    { id: 'team', label: 'My Team', icon: Users },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  // Mobile bottom tab bar items (5 key tabs)
  const bottomTabs = [
    { id: 'dashboard', label: 'Home', icon: Home },
    { id: 'assignments', label: 'Tasks', icon: ClipboardCheck, badge: pendingCount },
    { id: 'billing', label: 'Billing', icon: DollarSign },
    { id: 'production', label: 'Production', icon: Activity },
    { id: 'more', label: 'More', icon: MoreHorizontal },
  ];

  // Hover card helper
  const hoverCardStyle = (id) => ({
    ...tCard,
    transition: 'transform 0.2s, box-shadow 0.2s',
    transform: hoveredCard === id ? 'translateY(-2px)' : 'none',
    boxShadow: hoveredCard === id ? '0 8px 25px rgba(0,0,0,0.1)' : '0 2px 4px rgba(0,0,0,0.05)',
    cursor: 'pointer',
  });

  const [projectOptions, setProjectOptions] = useState([]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const text = await fetchWithRedirect(GATEWAY_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain;charset=utf-8' },
          body: JSON.stringify({
            secret: GATEWAY_SECRET,
            action: 'sheetsRead',
            params: { spreadsheetId: OPERATIONS_SHEET_ID, range: 'Projects!A2:A50' }
          })
        });
        const result = JSON.parse(text);
        if (result.success && result.data?.data) {
          setProjectOptions(result.data.data.flat().filter(p => p));
        }
      } catch (err) {
        setProjectOptions(['Highline - Smithville', 'IE Smart Systems - DFW']);
      }
    };
    fetchProjects();
  }, []);

  // Load assigned segments for this contractor (all projects)
  // Uses both segment assigned_to fields AND accepted assignments to find segments
  useEffect(() => {
    const loadMySegments = async () => {
      try {
        setSegmentsLoading(true);
        const data = await loadFullProject(null);
        if (data && data.segments) {
          const companyName = loggedInUser?.company || loggedInUser?.name || '';

          // Method 1: segments with assigned_to fields matching this contractor
          const byField = getContractorSegments(data.segments, companyName);

          // Method 2: segments referenced in accepted/pending assignments for this contractor
          const allAssignments = await loadAssignments(null);
          const myAssigns = getContractorAssignments(allAssignments, companyName);
          const assignedSegIds = new Set();
          for (const a of myAssigns) {
            if (a.status === 'Declined') continue;
            try {
              const ids = JSON.parse(a.segment_ids || '[]');
              ids.forEach(id => assignedSegIds.add(id));
            } catch (e) { /* skip bad JSON */ }
          }

          // Merge: all segments from field match + assignment match, deduplicated
          const fieldIds = new Set(byField.map(s => s.segment_id));
          const fromAssignments = data.segments.filter(s => assignedSegIds.has(s.segment_id) && !fieldIds.has(s.segment_id));
          const merged = [...byField, ...fromAssignments];

          // Detect phases and filter segments to only this contractor's assigned phases
          const phases = detectContractorPhases(merged, companyName);
          setMyPhases(phases);
          const phaseFiltered = filterSegmentsByPhase(merged, phases, companyName);

          setMySegments(phaseFiltered);
          setMyAssignments(myAssigns);

          // Load devices for organize mode — extract unique project_ids from contractor's segments
          const projectIds = [...new Set(merged.map(s => s.project_id).filter(Boolean))];
          if (projectIds.length > 0) {
            const allDevices = [];
            for (const pid of projectIds) {
              try {
                const devs = await loadDevices(pid);
                allDevices.push(...devs);
              } catch (e) { /* skip failed project */ }
            }
            // Filter devices to only types relevant to this contractor's phases
            setMyDevices(filterDevicesByPhase(allDevices, phases));
          }

          // Load map notes
          try {
            const notesPid = projectIds.length > 0 ? projectIds[0] : (loggedInUser?.project_id || 'BASTROP');
            const notes = await loadMapNotes(notesPid);
            setMapNotes(notes || []);
          } catch (e) { /* noop */ }
        }
      } catch (err) {
        console.error('[ContractorDashboard] Segment load error:', err);
      } finally {
        setSegmentsLoading(false);
      }
    };
    if (loggedInUser) {
      loadMySegments();
      loadMyAssignments();
    }
  }, [loggedInUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load line items when switching to billing tab
  const loadMyLineItems = async () => {
    setLineItemsLoading(true);
    try {
      const allItems = await loadLineItems(null);
      const mySegIds = new Set(mySegments.map(s => s.segment_id));
      const filtered = allItems.filter(li => mySegIds.has(li.segment_id) || (!li.segment_id && mySegIds.size > 0));
      setMyLineItems(filtered);
    } catch (err) {
      console.error('[ContractorDashboard] Line items load error:', err);
    } finally {
      setLineItemsLoading(false);
    }
  };

  // Load subs under this contractor
  const loadMySubs = useCallback(async () => {
    setSubsLoading(true);
    try {
      const text = await fetchWithRedirect(GATEWAY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({
          secret: GATEWAY_SECRET,
          action: 'sheetsRead',
          params: { spreadsheetId: '1OjSak2YJJvbXjyX3FSND_GfaQUZ2IQkFiMRgLuNfqVw', range: 'A:J' }
        })
      });
      const result = JSON.parse(text);
      if (result.success && result.data?.data) {
        const rows = result.data.data;
        const headers = rows[0];
        const subs = [];
        for (let i = 1; i < rows.length; i++) {
          const user = {};
          headers.forEach((h, j) => {
            if (h !== 'passwordHash' && h !== 'tempPassword') {
              user[h] = rows[i][j] || '';
            }
          });
          if (user.role === 'sub' && user.parent_email === loggedInUser?.email) {
            subs.push(user);
          }
        }
        setMySubs(subs);
      }
    } catch (err) {
      console.error('[ContractorDashboard] Subs load error:', err);
    } finally {
      setSubsLoading(false);
    }
  }, [loggedInUser]);

  // Add a sub-contractor under this contractor
  const handleAddSub = async () => {
    if (!newSub.name.trim() || !newSub.email.trim()) {
      setAddSubStatus({ show: true, success: false, message: 'Name and email are required.' });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newSub.email.trim())) {
      setAddSubStatus({ show: true, success: false, message: 'Please enter a valid email address.' });
      return;
    }
    setAddSubLoading(true);
    setAddSubStatus({ show: false, success: false, message: '' });
    try {
      const text = await fetchWithRedirect(PORTAL_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({
          action: 'createUser',
          email: newSub.email.trim().toLowerCase(),
          name: newSub.name.trim(),
          role: 'sub',
          company: newSub.company.trim() || loggedInUser?.company || '',
          parent_email: loggedInUser?.email || '',
          invitedBy: loggedInUser?.email || '',
        })
      });
      const result = JSON.parse(text);
      if (result.success) {
        const tempPw = result.tempPassword || result.data?.tempPassword || '';
        setAddSubStatus({
          show: true, success: true,
          message: `Sub-contractor added!${tempPw ? ` Temp password: ${tempPw}` : ' They can set their password at the login page.'}`
        });
        setNewSub({ name: '', email: '', company: '' });
        setShowAddSub(false);
        subsLoadedRef.current = false;
        loadMySubs();
      } else {
        setAddSubStatus({ show: true, success: false, message: result.message || 'Failed to create sub-contractor.' });
      }
    } catch (err) {
      console.error('[ContractorDashboard] Add sub error:', err);
      setAddSubStatus({ show: true, success: false, message: 'Connection error. Please try again.' });
    } finally {
      setAddSubLoading(false);
    }
  };

  // Load assignments for this contractor
  const loadMyAssignments = async () => {
    setAssignmentsLoading(true);
    try {
      const all = await loadAssignments(null);
      const companyName = loggedInUser?.company || loggedInUser?.name || '';
      const mine = getContractorAssignments(all, companyName);
      setMyAssignments(mine);
    } catch (err) {
      console.error('[ContractorDashboard] Assignments load error:', err);
    } finally {
      setAssignmentsLoading(false);
    }
  };

  // Load compliance data from Contractor Submissions sheet
  const loadComplianceData = useCallback(async () => {
    if (complianceLoading || complianceItems) return;
    setComplianceLoading(true);
    try {
      const text = await fetchWithRedirect(GATEWAY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({
          secret: GATEWAY_SECRET,
          action: 'sheetsRead',
          params: { spreadsheetId: OPERATIONS_SHEET_ID, range: 'Contractor Submissions!A:Z' }
        })
      });
      const result = JSON.parse(text);
      if (result.success && result.data?.data && result.data.data.length > 1) {
        const headers = result.data.data[0];
        const rows = result.data.data.slice(1);
        const emailIdx = headers.findIndex(h => h && h.toString().toLowerCase().includes('email'));
        const userRows = rows.filter(r => r[emailIdx] && r[emailIdx].toString().toLowerCase() === (loggedInUser?.email || '').toLowerCase());

        if (userRows.length > 0) {
          const userRow = userRows[userRows.length - 1]; // most recent
          const now = new Date();
          const thirtyDays = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

          const fields = [
            { key: 'coi_expiration', label: 'Certificate of Insurance (COI)' },
            { key: 'license_expiration', label: 'Business License' },
            { key: 'vehicle_insurance_exp', label: 'Vehicle Insurance' },
            { key: 'osha_expiration', label: 'OSHA Certification' },
            { key: 'flagger_cert_exp', label: 'Flagger Certification' },
          ];

          const items = fields.map(f => {
            const idx = headers.findIndex(h => h && h.toString().toLowerCase().includes(f.key));
            if (idx === -1 || !userRow[idx]) {
              return { label: f.label, status: 'Not on File', color: '#9E9E9E', expires: 'N/A' };
            }
            const expDate = new Date(userRow[idx]);
            if (isNaN(expDate.getTime())) {
              return { label: f.label, status: 'Not on File', color: '#9E9E9E', expires: 'N/A' };
            }
            const daysLeft = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
            if (expDate < now) {
              return { label: f.label, status: 'EXPIRED', color: '#e85a4f', expires: expDate.toLocaleDateString(), daysLeft };
            } else if (daysLeft <= 30) {
              return { label: f.label, status: `Expires in ${daysLeft}d`, color: '#FFB800', expires: expDate.toLocaleDateString(), daysLeft };
            } else {
              return { label: f.label, status: 'Current', color: '#4CAF50', expires: expDate.toLocaleDateString(), daysLeft };
            }
          });

          // Check W-9 and MSA presence
          const w9Idx = headers.findIndex(h => h && h.toString().toLowerCase().includes('w9'));
          const msaIdx = headers.findIndex(h => h && (h.toString().toLowerCase().includes('msa') || h.toString().toLowerCase().includes('master_sub')));

          items.push({
            label: 'W-9',
            status: (w9Idx !== -1 && userRow[w9Idx]) ? 'On File' : 'Not Submitted',
            color: (w9Idx !== -1 && userRow[w9Idx]) ? '#4CAF50' : '#9E9E9E',
            expires: 'N/A',
          });
          items.push({
            label: 'Master Subcontractor Agreement',
            status: (msaIdx !== -1 && userRow[msaIdx]) ? 'Signed' : 'Not Submitted',
            color: (msaIdx !== -1 && userRow[msaIdx]) ? '#4CAF50' : '#9E9E9E',
            expires: 'N/A',
          });

          setComplianceItems(items);
        } else {
          setComplianceItems([]);
        }
      } else {
        setComplianceItems([]);
      }
    } catch (err) {
      console.error('[ContractorDashboard] Compliance load error:', err);
      setComplianceItems([]);
    } finally {
      setComplianceLoading(false);
    }
  }, [loggedInUser, complianceLoading, complianceItems]);

  // Load invoices from DB
  const loadMyInvoices = useCallback(async () => {
    if (invoicesLoading) return;
    setInvoicesLoading(true);
    try {
      const email = encodeURIComponent(loggedInUser?.email || '');
      const resp = await fetch(`/api/payment-invoices?submitted_by=${email}`);
      const data = await resp.json();
      if (data.success && Array.isArray(data.data)) {
        setMyInvoices(data.data);
      }
    } catch (err) {
      console.error('[ContractorDashboard] Invoice load error:', err);
    } finally {
      setInvoicesLoading(false);
    }
  }, [loggedInUser, invoicesLoading]);

  // Phase-aware status field (e.g. 'boring_status', 'pulling_status', 'splicing_status')
  const statusField = getPhaseStatusField(myPhases);

  // Segment stats
  const segStats = {
    total: mySegments.length,
    completed: mySegments.filter(s => s[statusField] === 'QC Approved' || s[statusField] === 'Complete').length,
    inProgress: mySegments.filter(s => s[statusField] === 'In Progress').length,
    notStarted: mySegments.filter(s => s[statusField] === 'Not Started').length,
    issues: mySegments.filter(s => s[statusField] === 'Issue').length,
    totalFootage: mySegments.reduce((sum, s) => sum + (parseFloat(s.footage) || 0), 0),
  };

  // Derived section list from segments + devices (with per-section stats)
  const existingSections = useMemo(() => {
    const sectionMap = {};
    const initSection = (name) => {
      if (!sectionMap[name]) sectionMap[name] = { name, segments: 0, devices: 0, footage: 0, completed: 0, inProgress: 0, issues: 0 };
    };
    mySegments.forEach(s => {
      const name = s.section || '';
      if (!name) return;
      initSection(name);
      sectionMap[name].segments++;
      sectionMap[name].footage += parseFloat(s.footage) || 0;
      if (s[statusField] === 'Complete' || s[statusField] === 'QC Approved') sectionMap[name].completed++;
      else if (s[statusField] === 'In Progress') sectionMap[name].inProgress++;
      else if (s[statusField] === 'Issue') sectionMap[name].issues++;
    });
    myDevices.forEach(d => {
      const name = d.section || '';
      if (!name) return;
      initSection(name);
      sectionMap[name].devices++;
    });
    // Add unsectioned entry if any segments have no section
    const unsectioned = mySegments.filter(s => !s.section);
    const unsectionedDevices = myDevices.filter(d => !d.section);
    if (unsectioned.length > 0 || unsectionedDevices.length > 0) {
      sectionMap[''] = {
        name: '',
        segments: unsectioned.length,
        devices: unsectionedDevices.length,
        footage: unsectioned.reduce((sum, s) => sum + (parseFloat(s.footage) || 0), 0),
        completed: unsectioned.filter(s => s[statusField] === 'Complete' || s[statusField] === 'QC Approved').length,
        inProgress: unsectioned.filter(s => s[statusField] === 'In Progress').length,
        issues: unsectioned.filter(s => s[statusField] === 'Issue').length,
      };
    }
    return Object.values(sectionMap).sort((a, b) => {
      if (a.name === '') return 1;
      if (b.name === '') return -1;
      return a.name.localeCompare(b.name);
    });
  }, [mySegments, myDevices, statusField]);

  // Sub-assignment map: sub email → { name, segmentIds: Set, color }
  const SUB_COLORS = ['#00BCD4', '#FF9800', '#8BC34A', '#E91E63', '#3F51B5', '#CDDC39', '#FF5722', '#009688', '#673AB7', '#FFC107'];
  const subAssignmentMap = useMemo(() => {
    const map = {};
    // Build from assignments that have assigned_to_sub
    myAssignments.forEach(a => {
      if (!a.assigned_to_sub || a.status === 'Declined') return;
      const email = a.assigned_to_sub;
      if (!map[email]) map[email] = { email, name: '', segmentIds: new Set() };
      try {
        const ids = JSON.parse(a.segment_ids || '[]');
        ids.forEach(id => map[email].segmentIds.add(id));
      } catch (e) { /* skip bad JSON */ }
    });
    // Enrich names from mySubs
    mySubs.forEach(sub => {
      if (map[sub.email]) map[sub.email].name = sub.name || sub.email;
    });
    // Assign colors and fill in missing names
    const entries = Object.values(map);
    entries.forEach((entry, i) => {
      entry.color = SUB_COLORS[i % SUB_COLORS.length];
      if (!entry.name) entry.name = entry.email;
    });
    return map;
  }, [myAssignments, mySubs]);

  // All sub-assigned segment IDs (union)
  const allSubAssignedIds = useMemo(() => {
    const ids = new Set();
    Object.values(subAssignmentMap).forEach(sub => sub.segmentIds.forEach(id => ids.add(id)));
    return ids;
  }, [subAssignmentMap]);

  // Invoice stats
  const invoiceStats = {
    total: myInvoices.length,
    submitted: myInvoices.filter(i => i.status === 'Submitted').length,
    approved: myInvoices.filter(i => i.status === 'Approved').length,
    paid: myInvoices.filter(i => i.status === 'Paid').length,
    totalAmount: myInvoices.reduce((sum, i) => sum + (parseFloat(i.subtotal) || 0), 0),
    totalRetainage: myInvoices.reduce((sum, i) => sum + (parseFloat(i.retainage_amount) || 0), 0),
    totalNet: myInvoices.reduce((sum, i) => sum + (parseFloat(i.net_amount) || 0), 0),
    totalPaid: myInvoices.filter(i => i.status === 'Paid').reduce((sum, i) => sum + (parseFloat(i.net_amount) || 0), 0),
  };

  // Street sheet submit
  const handleStreetSheetSubmit = async () => {
    if (!streetSheetForm.project) {
      showNotification(false, 'Please select a project');
      return;
    }
    setLoading(true);

    // Submit crew rows
    for (const crew of streetSheetForm.crews) {
      if (!crew.leader && !crew.street) continue;
      await submitToSheet('Street Sheets', [
        new Date().toISOString(),
        loggedInUser?.email || '',
        loggedInUser?.company || loggedInUser?.name || '',
        streetSheetForm.county || '',
        streetSheetForm.project,
        streetSheetForm.date,
        'Crew',
        crew.leader,
        crew.street,
        crew.cabinet,
        crew.boreInfo,
        '', '', '', '',
      ]);
    }

    // Submit prior day rows
    for (const row of streetSheetForm.priorDay) {
      if (!row.leader && !row.street) continue;
      await submitToSheet('Street Sheets', [
        new Date().toISOString(),
        loggedInUser?.email || '',
        loggedInUser?.company || loggedInUser?.name || '',
        streetSheetForm.county || '',
        streetSheetForm.project,
        streetSheetForm.date,
        'Prior Day',
        row.leader,
        row.street,
        row.cabinet,
        '',
        row.activity,
        row.qty || '0',
        row.completed ? 'Yes' : 'No',
        '',
      ]);
    }

    setLoading(false);
    showNotification(true, 'Street sheet submitted!');
    setStreetSheetSubmittedToday(true);
    try {
      localStorage.setItem(todayKey, 'true');
      localStorage.setItem('streetsheet-last-submitted', new Date().toISOString());
    } catch (e) { /* noop */ }
    setStreetSheetForm({
      county: '', project: '', date: new Date().toISOString().slice(0, 10),
      crews: [{ leader: '', street: '', cabinet: '', boreInfo: '' }],
      priorDay: [{ leader: '', activity: 'Bore', qty: '', cabinet: '', street: '', completed: false }],
    });
  };

  // Card style helper (theme-based)
  const cardStyle = {
    ...tCard,
    padding: '24px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
    transition: 'transform 0.2s, box-shadow 0.2s',
  };

  // Status badge helper — pill shape
  const statusBadge = (status) => {
    const map = {
      Draft: { bg: '#9E9E9E20', fg: '#9E9E9E' },
      Submitted: { bg: '#2196F320', fg: '#2196F3' },
      Approved: { bg: '#4CAF5020', fg: '#4CAF50' },
      Paid: { bg: '#00b4d820', fg: '#00b4d8' },
      Rejected: { bg: '#e85a4f20', fg: '#e85a4f' },
    };
    const s = map[status] || map.Draft;
    return (
      <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700, backgroundColor: s.bg, color: s.fg }}>
        {status || 'Draft'}
      </span>
    );
  };

  // ========== RENDER FUNCTIONS ==========

  const renderDashboard = () => (
    <div>
      {/* Gradient Header Card */}
      <div style={{
        background: tc.gradientPortal, borderRadius: '16px', padding: isMobile ? '24px 20px' : '32px',
        marginBottom: '24px', color: '#fff', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <h2 style={{ fontSize: isMobile ? '1.5rem' : '1.75rem', fontWeight: '700', marginBottom: '4px', color: '#fff' }}>
            Welcome, {loggedInUser?.name?.split(' ')[0] || 'Contractor'}!
          </h2>
          <p style={{ opacity: 0.85, fontSize: '0.9rem', margin: 0 }}>
            {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
          {loggedInUser?.company && (
            <p style={{ opacity: 0.7, fontSize: '0.8rem', marginTop: '4px' }}>{loggedInUser.company}</p>
          )}
          {/* Street Sheet Banner — integrated into header */}
          {!streetSheetSubmittedToday && (
            <button onClick={() => setActiveTab('street-sheet')} style={{
              marginTop: '16px', display: 'inline-flex', alignItems: 'center', gap: '8px',
              background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '8px', padding: '10px 16px', color: '#fff', cursor: 'pointer',
              fontSize: '0.85rem', fontWeight: 600, backdropFilter: 'blur(4px)',
            }}>
              <Shovel size={16} /> Street sheet not submitted today — Submit Now
            </button>
          )}
        </div>
        <div style={{ position: 'absolute', right: '-20px', top: '-20px', width: '120px', height: '120px', borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
        <div style={{ position: 'absolute', right: '40px', bottom: '-30px', width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
      </div>

      {/* Pending Assignments Alert Banner */}
      {pendingCount > 0 && (
        <div style={{
          borderRadius: 12, padding: '16px 20px', marginBottom: '24px',
          borderLeft: '4px solid #FFB800',
          background: darkMode ? '#FFB80010' : '#FFF8E1',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <AlertTriangle size={18} color="#FFB800" />
          <span style={{ flex: 1, fontSize: '0.9rem', color: tc.text }}>
            You have <strong>{pendingCount}</strong> new assignment{pendingCount !== 1 ? 's' : ''} waiting for review.
          </span>
          <button onClick={() => setActiveTab('assignments')} style={{
            background: 'transparent', border: 'none', color: tc.accentPrimary,
            fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem', whiteSpace: 'nowrap',
          }}>
            View Assignments →
          </button>
        </div>
      )}

      {/* Stats Row — 4 cards with colored top border */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: '16px', marginBottom: '32px' }}>
        {[
          { label: 'Segments', value: segmentsLoading ? null : String(segStats.total), subtext: segmentsLoading ? '—' : `${Math.round(segStats.totalFootage).toLocaleString()} LF`, color: tc.accentPrimary },
          { label: 'Completed', value: segmentsLoading ? null : String(segStats.completed), subtext: segmentsLoading ? '—' : `of ${segStats.total}`, color: tc.success },
          { label: 'In Progress', value: segmentsLoading ? null : String(segStats.inProgress), subtext: segmentsLoading ? '—' : 'active', color: tc.warning },
          { label: 'Issues', value: segmentsLoading ? null : String(segStats.issues), subtext: segmentsLoading ? '—' : (segStats.issues > 0 ? 'Needs attention' : 'All clear'), color: segStats.issues > 0 ? tc.error : tc.success },
        ].map((stat, idx) => (
          <div key={idx} style={{ ...cardStyle, padding: '20px', borderTop: `3px solid ${stat.color}` }}
            onMouseEnter={() => setHoveredCard(`stat-${idx}`)} onMouseLeave={() => setHoveredCard(null)}>
            {stat.value === null ? (
              <Skeleton width="50px" height="32px" style={{ marginBottom: '8px' }} />
            ) : (
              <div style={{ fontSize: '2rem', fontWeight: '700', color: tc.text, lineHeight: 1 }}>{stat.value}</div>
            )}
            <div style={{ fontSize: '0.85rem', fontWeight: 600, color: tc.text, marginTop: '8px' }}>{stat.label}</div>
            <div style={{ fontSize: '0.75rem', color: tc.textMuted }}>{stat.subtext}</div>
          </div>
        ))}
      </div>

      {/* Quick Actions — 2 prominent CTAs + secondary links */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
        {[
          { label: 'View Map', subtext: 'See all your assigned segments', icon: Map, color: '#2196F3', action: () => setCurrentPage('project-map') },
          { label: 'Log Production', subtext: 'Record daily crew output', icon: Activity, color: tc.accentPrimary, action: () => setActiveTab('production') },
        ].map((cta, idx) => (
          <button key={idx} onClick={cta.action}
            onMouseEnter={() => setHoveredCard(`cta-${idx}`)} onMouseLeave={() => setHoveredCard(null)}
            style={{
              ...hoverCardStyle(`cta-${idx}`), padding: '24px', border: 'none', textAlign: 'left',
              display: 'flex', alignItems: 'center', gap: '16px',
            }}>
            <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: `${cta.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <cta.icon size={26} color={cta.color} />
            </div>
            <div>
              <span style={{ fontWeight: '600', fontSize: '1.05rem', color: tc.text, display: 'block' }}>{cta.label}</span>
              <span style={{ fontSize: '0.8rem', color: tc.textMuted }}>{cta.subtext}</span>
            </div>
            <ChevronRight size={18} color={tc.textMuted} style={{ marginLeft: 'auto' }} />
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '12px', marginBottom: '32px', flexWrap: 'wrap' }}>
        {[
          { label: 'Assignments', badge: pendingCount, tab: 'assignments' },
          { label: 'Street Sheet', tab: 'street-sheet' },
          { label: 'Compliance', tab: 'compliance' },
        ].map((link, idx) => (
          <button key={idx} onClick={() => setActiveTab(link.tab)} style={{
            ...getButtonStyle('outline', darkMode), padding: '8px 16px', fontSize: '0.85rem',
            display: 'flex', alignItems: 'center', gap: '6px',
          }}>
            {link.label}
            {link.badge > 0 && <span style={{ background: '#FFB800', color: '#fff', borderRadius: '10px', padding: '1px 7px', fontSize: '0.7rem', fontWeight: 700 }}>{link.badge}</span>}
          </button>
        ))}
      </div>

      {/* Assigned Segments */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: tc.text, margin: 0 }}>Assigned Segments</h3>
          <button onClick={() => setCurrentPage('project-map')} style={{ background: 'none', border: 'none', color: tc.accentPrimary, cursor: 'pointer', fontSize: '0.85rem', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px' }}>
            View Map <ChevronRight size={14} />
          </button>
        </div>
        {segmentsLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {[1,2,3].map(i => (
              <div key={i} style={{ ...cardStyle, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Skeleton width="8px" height="8px" style={{ borderRadius: '50%', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <Skeleton width="60%" height="14px" style={{ marginBottom: '6px' }} />
                  <Skeleton width="40%" height="10px" />
                </div>
                <Skeleton width="70px" height="22px" />
              </div>
            ))}
          </div>
        ) : mySegments.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', ...cardStyle }}>
            <MapPin size={56} color={tc.textMuted} style={{ marginBottom: '16px', opacity: 0.3 }} />
            <div style={{ color: tc.text, fontWeight: 500, marginBottom: '4px' }}>No segments assigned yet</div>
            <div style={{ fontSize: '0.85rem', color: tc.textMuted }}>Segments will appear here once management assigns work to you.</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {mySegments.slice(0, 5).map((seg, idx) => {
              const statusColors = { 'QC Approved': '#4CAF50', 'Complete': '#4CAF50', 'In Progress': '#FFB800', 'Not Started': '#9E9E9E', 'Issue': '#FF9800' };
              const sColor = statusColors[seg[statusField]] || '#9E9E9E';
              return (
                <div key={idx} onClick={() => setCurrentPage('project-map')}
                  onMouseEnter={() => setHoveredCard(`seg-${idx}`)} onMouseLeave={() => setHoveredCard(null)}
                  style={{
                    backgroundColor: tc.cardBg, borderRadius: '10px', padding: '14px 16px',
                    display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer',
                    borderLeft: `3px solid ${sColor}`,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    transform: hoveredCard === `seg-${idx}` ? 'translateY(-2px)' : 'none',
                    boxShadow: hoveredCard === `seg-${idx}` ? '0 6px 20px rgba(0,0,0,0.1)' : '0 1px 3px rgba(0,0,0,0.06)',
                  }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: sColor, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', fontSize: '0.9rem', color: tc.text }}>{seg.segment_id || seg.contractor_id || `${seg.from_structure || '?'} → ${seg.to_structure || '?'}`}</div>
                    <div style={{ fontSize: '0.75rem', color: tc.textMuted }}>{seg.street || seg.section || 'Unknown'} • {Math.round(parseFloat(seg.footage) || 0)} LF</div>
                  </div>
                  <div style={{ fontSize: '0.7rem', fontWeight: '600', padding: '3px 10px', borderRadius: '20px', backgroundColor: `${sColor}15`, color: sColor }}>{seg[statusField] || 'Unknown'}</div>
                </div>
              );
            })}
            {mySegments.length > 5 && (
              <button onClick={() => setCurrentPage('project-map')} style={{ background: 'none', border: `1px dashed ${tc.textMuted}40`, borderRadius: '10px', padding: '12px', color: tc.textMuted, cursor: 'pointer', fontSize: '0.85rem' }}>
                +{mySegments.length - 5} more segments — View all on map
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // Unified status update handler — works for single or batch
  const handleStatusUpdate = async (newStatus) => {
    if (selectedSegIds.size === 0) return;
    setStatusUpdating(true);
    const now = new Date().toISOString();
    const phasePrefix = statusField.replace('_status', '');
    const tsField = newStatus === 'Complete' ? `${phasePrefix}_completed` : newStatus === 'In Progress' ? `${phasePrefix}_started` : null;

    let successCount = 0;
    const ids = [...selectedSegIds];
    for (const segId of ids) {
      const ok = await updateSegmentField(segId, statusField, newStatus);
      if (ok) {
        if (tsField) updateSegmentField(segId, tsField, now);
        successCount++;
      }
    }

    const updates = { [statusField]: newStatus };
    if (tsField) updates[tsField] = now;
    setMySegments(prev => prev.map(s =>
      selectedSegIds.has(s.segment_id) ? { ...s, ...updates } : s
    ));

    setSelectedSegIds(new Set());
    setSelectedDevIds(new Set());
    setStatusUpdating(false);
    showNotification(true, `${successCount} segment${successCount !== 1 ? 's' : ''} → ${newStatus}`);
  };

  // Assign selected items to a section
  const handleAssignSection = async (name) => {
    if (!name.trim()) return;
    setAssigningSection(true);
    const segIds = [...selectedSegIds];
    const devIds = [...selectedDevIds];
    const result = await bulkUpdateSection(segIds, devIds, name.trim());

    setMySegments(prev => prev.map(s =>
      selectedSegIds.has(s.segment_id) ? { ...s, section: name.trim() } : s
    ));
    setMyDevices(prev => prev.map(d =>
      selectedDevIds.has(d.device_id) ? { ...d, section: name.trim() } : d
    ));

    setSelectedSegIds(new Set());
    setSelectedDevIds(new Set());
    setSectionInput('');
    setAssigningSection(false);
    setShowSectionAssign(false);
    showNotification(true, `Assigned ${result.segmentsUpdated} segments + ${result.devicesUpdated} devices to "${name.trim()}"`);
  };

  // Team tab: assign sections to a sub
  const handleAssignToSub = async (sub) => {
    if (selectedSubSections.size === 0) return;
    setAssigningToSub(true);

    // Gather all segment IDs in the checked sections
    const segIds = mySegments
      .filter(s => selectedSubSections.has(s.section || ''))
      .map(s => s.segment_id);

    const projectIds = [...new Set(mySegments.filter(s => selectedSubSections.has(s.section || '')).map(s => s.project_id).filter(Boolean))];

    const result = await createSubAssignment({
      projectId: projectIds[0] || '',
      segmentIds: segIds,
      subEmail: sub.email,
      subName: sub.name,
      contractorEmail: loggedInUser?.email || '',
      contractorName: loggedInUser?.company || loggedInUser?.name || '',
      sections: [...selectedSubSections],
      workType: 'boring',
    });

    setAssigningToSub(false);
    setAssignSubOpen(null);
    setSelectedSubSections(new Set());

    if (result.success) {
      showNotification(true, `Assigned ${selectedSubSections.size} section(s) to ${sub.name}`);
    } else {
      showNotification(false, 'Assignment failed — try again');
    }
  };

  const onRectangleComplete = (rect) => {
    const bounds = rect.getBounds();
    if (!bounds) { rect.setMap(null); return; }
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const newSegIds = new Set(selectedSegIds);
    const newDevIds = new Set(selectedDevIds);
    const inBounds = (lat, lng) => lat >= sw.lat() && lat <= ne.lat() && lng >= sw.lng() && lng <= ne.lng();
    const isAll = selectedSection === '__ALL__';
    const segs = isAll ? mySegments : mySegments.filter(s => (s.section || '') === selectedSection);
    const devs = isAll ? myDevices : myDevices.filter(d => (d.section || '') === selectedSection);
    (segs || []).forEach(s => {
      const slat = parseFloat(s.gps_start_lat); const slng = parseFloat(s.gps_start_lng);
      const elat = parseFloat(s.gps_end_lat); const elng = parseFloat(s.gps_end_lng);
      if ((!isNaN(slat) && !isNaN(slng) && inBounds(slat, slng)) || (!isNaN(elat) && !isNaN(elng) && inBounds(elat, elng))) {
        newSegIds.add(s.segment_id);
      }
    });
    (devs || []).forEach(d => {
      const lat = parseFloat(d.latitude || d.lat); const lng = parseFloat(d.longitude || d.lng);
      if (!isNaN(lat) && !isNaN(lng) && inBounds(lat, lng)) newDevIds.add(d.device_id);
    });
    setSelectedSegIds(newSegIds);
    setSelectedDevIds(newDevIds);
    setDrawRectMode(false);
    rect.setMap(null);
  };

  const getProdColor = getMapStatusColor;

  // Auto-zoom map when selectedSection changes
  useEffect(() => {
    if (!prodMapRef.current || !selectedSection) return;
    const isAllMode = selectedSection === '__ALL__';
    const sectionSegs = isAllMode ? mySegments : mySegments.filter(s => (s.section || '') === selectedSection);
    const mappable = sectionSegs.filter(s => {
      const slat = parseFloat(s.gps_start?.lat ?? s.gps_start_lat);
      const slng = parseFloat(s.gps_start?.lng ?? s.gps_start_lng);
      const elat = parseFloat(s.gps_end?.lat ?? s.gps_end_lat);
      const elng = parseFloat(s.gps_end?.lng ?? s.gps_end_lng);
      return !isNaN(slat) && !isNaN(slng) && !isNaN(elat) && !isNaN(elng) && (slat !== 0 || slng !== 0);
    });
    if (mappable.length === 0) return;
    const bounds = new window.google.maps.LatLngBounds();
    mappable.forEach(s => {
      bounds.extend({ lat: parseFloat(s.gps_start?.lat ?? s.gps_start_lat), lng: parseFloat(s.gps_start?.lng ?? s.gps_start_lng) });
      bounds.extend({ lat: parseFloat(s.gps_end?.lat ?? s.gps_end_lat), lng: parseFloat(s.gps_end?.lng ?? s.gps_end_lng) });
    });
    prodMapRef.current.fitBounds(bounds, 40);
  }, [selectedSection, mySegments]);

  const renderProduction = () => {
    // Loading / empty states
    if (segmentsLoading) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: tc.textMuted }}>
          <Loader size={24} style={{ animation: 'spin 1s linear infinite', marginRight: '10px' }} /> Loading segments...
        </div>
      );
    }

    if (mySegments.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <MapPin size={64} color={tc.textMuted} style={{ marginBottom: '16px', opacity: 0.3 }} />
          <h2 style={{ color: tc.text, fontWeight: 600, marginBottom: '8px' }}>No segments assigned</h2>
          <p style={{ color: tc.textMuted }}>Segments will appear here once management assigns work to you.</p>
        </div>
      );
    }

    // ── View A: Section List (selectedSection === null) ──
    if (selectedSection === null) {
      const totalSections = existingSections.filter(s => s.name !== '').length;
      const totalFootage = mySegments.reduce((sum, s) => sum + (parseFloat(s.footage) || 0), 0);

      return (
        <div>
          {/* Header */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: tc.text, margin: 0 }}>My Sections</h2>
              <button onClick={() => { setSelectedSection('__ALL__'); setSelectedSegIds(new Set()); setSelectedDevIds(new Set()); }} style={{
                display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px',
                backgroundColor: tc.accentPrimary, color: '#fff', border: 'none', borderRadius: '8px',
                fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', minHeight: '48px',
              }}>
                <Map size={15} />
                View All
              </button>
            </div>
            <p style={{ color: tc.textMuted, fontSize: '0.85rem' }}>
              {totalSections} section{totalSections !== 1 ? 's' : ''} &middot; {mySegments.length} segments &middot; {totalFootage.toLocaleString()} LF
            </p>
          </div>

          {/* Section cards */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {existingSections.map(sec => {
              const pct = sec.segments > 0 ? Math.round((sec.completed / sec.segments) * 100) : 0;
              const notStarted = sec.segments - sec.completed - sec.inProgress - sec.issues;
              const displayName = sec.name || 'Unsectioned';

              return (
                <div
                  key={sec.name}
                  onClick={() => { setSelectedSection(sec.name); setSelectedSegIds(new Set()); setSelectedDevIds(new Set()); }}
                  style={{
                    ...tCard,
                    padding: '16px',
                    cursor: 'pointer',
                    transition: 'transform 0.15s, box-shadow 0.15s',
                    ...(hoveredCard === `sec-${sec.name}` ? { transform: 'translateY(-1px)', boxShadow: `0 4px 16px ${darkMode ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.1)'}` } : {}),
                  }}
                  onMouseEnter={() => setHoveredCard(`sec-${sec.name}`)}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  {/* Top row: name + chevron */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ fontSize: '1.05rem', fontWeight: 700, color: sec.name ? tc.text : tc.textMuted }}>
                      {displayName}
                    </span>
                    <ChevronRight size={18} color={tc.textMuted} />
                  </div>

                  {/* Subtitle: counts */}
                  <div style={{ fontSize: '0.8rem', color: tc.textMuted, marginBottom: '10px' }}>
                    {sec.segments} segment{sec.segments !== 1 ? 's' : ''} &middot; {sec.devices} device{sec.devices !== 1 ? 's' : ''} &middot; {sec.footage.toLocaleString()} LF
                  </div>

                  {/* Progress bar */}
                  <div style={{ height: '6px', borderRadius: '3px', backgroundColor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', overflow: 'hidden', display: 'flex' }}>
                    {sec.completed > 0 && (
                      <div style={{ width: `${(sec.completed / sec.segments) * 100}%`, backgroundColor: STATUS_COLORS.COMPLETE, transition: 'width 0.3s' }} />
                    )}
                    {sec.inProgress > 0 && (
                      <div style={{ width: `${(sec.inProgress / sec.segments) * 100}%`, backgroundColor: STATUS_COLORS.IN_PROGRESS, transition: 'width 0.3s' }} />
                    )}
                    {sec.issues > 0 && (
                      <div style={{ width: `${(sec.issues / sec.segments) * 100}%`, backgroundColor: '#FF5722', transition: 'width 0.3s' }} />
                    )}
                    {notStarted > 0 && (
                      <div style={{ width: `${(notStarted / sec.segments) * 100}%`, backgroundColor: '#9E9E9E', opacity: 0.4, transition: 'width 0.3s' }} />
                    )}
                  </div>

                  {/* Percentage label */}
                  <div style={{ fontSize: '0.75rem', color: tc.textMuted, marginTop: '6px', display: 'flex', justifyContent: 'space-between' }}>
                    <span>{pct}% complete</span>
                    <span style={{ display: 'flex', gap: '8px' }}>
                      {sec.completed > 0 && <span style={{ color: STATUS_COLORS.COMPLETE }}>{sec.completed} done</span>}
                      {sec.inProgress > 0 && <span style={{ color: STATUS_COLORS.IN_PROGRESS }}>{sec.inProgress} active</span>}
                      {sec.issues > 0 && <span style={{ color: '#FF5722' }}>{sec.issues} issue{sec.issues !== 1 ? 's' : ''}</span>}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    // ── View B: Section Map (selectedSection !== null) ──
    if (!mapsLoaded) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: tc.textMuted }}>
          <Loader size={24} style={{ animation: 'spin 1s linear infinite', marginRight: '10px' }} /> Loading map...
        </div>
      );
    }

    // Filter to this section's segments (or all items when __ALL__)
    const isAllMode = selectedSection === '__ALL__';
    const sectionSegs = isAllMode ? mySegments : mySegments.filter(s => (s.section || '') === selectedSection);
    const sectionDevices = isAllMode ? myDevices : myDevices.filter(d => (d.section || '') === selectedSection);

    const mappableSegs = sectionSegs.filter(s => {
      const slat = parseFloat(s.gps_start?.lat ?? s.gps_start_lat);
      const slng = parseFloat(s.gps_start?.lng ?? s.gps_start_lng);
      const elat = parseFloat(s.gps_end?.lat ?? s.gps_end_lat);
      const elng = parseFloat(s.gps_end?.lng ?? s.gps_end_lng);
      return !isNaN(slat) && !isNaN(slng) && !isNaN(elat) && !isNaN(elng) && (slat !== 0 || slng !== 0);
    });

    const prodStats = {
      total: sectionSegs.length,
      complete: sectionSegs.filter(s => s[statusField] === 'Complete' || s[statusField] === 'QC Approved').length,
      inProgress: sectionSegs.filter(s => s[statusField] === 'In Progress').length,
      issues: sectionSegs.filter(s => s[statusField] === 'Issue').length,
    };

    // Fit bounds scoped to this section
    const onMapLoad = (map) => {
      prodMapRef.current = map;
      if (mappableSegs.length === 0) return;
      const bounds = new window.google.maps.LatLngBounds();
      mappableSegs.forEach(s => {
        bounds.extend({ lat: parseFloat(s.gps_start?.lat ?? s.gps_start_lat), lng: parseFloat(s.gps_start?.lng ?? s.gps_start_lng) });
        bounds.extend({ lat: parseFloat(s.gps_end?.lat ?? s.gps_end_lat), lng: parseFloat(s.gps_end?.lng ?? s.gps_end_lng) });
      });
      map.fitBounds(bounds, 40);
    };

    const mapHeight = isMobile ? 'calc(100vh - 112px)' : 'calc(100vh - 80px)';

    // Devices with valid GPS for organize mode markers
    const mappableDevices = sectionDevices.filter(d => {
      const lat = parseFloat(d.gps_lat);
      const lng = parseFloat(d.gps_lng);
      return !isNaN(lat) && !isNaN(lng) && (lat !== 0 || lng !== 0);
    });

    // Visibility-filtered lists (status/device type toggles + view mode)
    const visibleSegs = mappableSegs.filter(s => {
      if (hiddenStatuses.has(s[statusField] || 'Not Started')) return false;
      // View mode filtering
      if (mapViewMode === 'lyt-only') {
        if (allSubAssignedIds.has(s.segment_id)) return false;
      } else if (mapViewMode === 'all-subs') {
        if (!allSubAssignedIds.has(s.segment_id)) return false;
      } else if (mapViewMode !== 'all' && mapViewMode.includes('@')) {
        // Specific sub email
        const sub = subAssignmentMap[mapViewMode];
        if (!sub || !sub.segmentIds.has(s.segment_id)) return false;
      }
      return true;
    });
    const visibleDevices = mappableDevices.filter(d => {
      const dtype = d.device_type || 'splice';
      if (hiddenDeviceTypes.has(dtype)) return false;
      return true;
    });

    // Get segment color based on view mode
    const getSegColor = (seg, isOrgSelected) => {
      if (isOrgSelected) return '#FFD700';
      if (mapViewMode === 'all-subs') {
        // Color by which sub owns it
        for (const sub of Object.values(subAssignmentMap)) {
          if (sub.segmentIds.has(seg.segment_id)) return sub.color;
        }
        return '#9E9E9E';
      }
      if (mapViewMode !== 'all' && mapViewMode.includes('@')) {
        const sub = subAssignmentMap[mapViewMode];
        return sub ? sub.color : getProdColor(seg[statusField]);
      }
      return getProdColor(seg[statusField]);
    };

    // View mode options for the selector
    const subEntries = Object.values(subAssignmentMap);
    const viewModeOptions = [
      { value: 'all', label: 'All Segments' },
      { value: 'lyt-only', label: 'LYT Only', desc: `${mappableSegs.length - allSubAssignedIds.size} segs` },
      ...subEntries.map(s => ({ value: s.email, label: s.name, desc: `${s.segmentIds.size} segs`, color: s.color })),
      ...(subEntries.length > 0 ? [{ value: 'all-subs', label: 'All Subs (colored)', desc: `${allSubAssignedIds.size} segs` }] : []),
    ];

    const orgSelCount = selectedSegIds.size + selectedDevIds.size;

    // Box select: convert pixel rect to lat/lng and select visible items inside
    const finishBoxSelect = (startX, startY, endX, endY) => {
      const map = prodMapRef.current;
      if (!map || !boxRef.current) return;
      const bounds = map.getBounds();
      if (!bounds) return;
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      const container = boxRef.current;
      const w = container.offsetWidth;
      const h = container.offsetHeight;
      // Pixel → lat/lng (linear interpolation)
      const minPx = Math.min(startX, endX);
      const maxPx = Math.max(startX, endX);
      const minPy = Math.min(startY, endY);
      const maxPy = Math.max(startY, endY);
      const west = sw.lng() + (minPx / w) * (ne.lng() - sw.lng());
      const east = sw.lng() + (maxPx / w) * (ne.lng() - sw.lng());
      const north = ne.lat() - (minPy / h) * (ne.lat() - sw.lat());
      const south = ne.lat() - (maxPy / h) * (ne.lat() - sw.lat());
      const inRect = (lat, lng) => lat >= south && lat <= north && lng >= west && lng <= east;
      // Select visible segments whose start OR end is in rect
      const newSegs = new Set(selectedSegIds);
      visibleSegs.forEach(s => {
        const slat = parseFloat(s.gps_start?.lat ?? s.gps_start_lat);
        const slng = parseFloat(s.gps_start?.lng ?? s.gps_start_lng);
        const elat = parseFloat(s.gps_end?.lat ?? s.gps_end_lat);
        const elng = parseFloat(s.gps_end?.lng ?? s.gps_end_lng);
        if (inRect(slat, slng) || inRect(elat, elng)) newSegs.add(s.segment_id);
      });
      // Select visible devices in rect
      const newDevs = new Set(selectedDevIds);
      visibleDevices.forEach(d => {
        const lat = parseFloat(d.gps_lat);
        const lng = parseFloat(d.gps_lng);
        if (inRect(lat, lng)) newDevs.add(d.device_id);
      });
      setSelectedSegIds(newSegs);
      setSelectedDevIds(newDevs);
    };

    // Mouse handlers for box select (desktop: shift+drag)
    const handleBoxMouseDown = (e) => {
      if (!e.shiftKey) return;
      e.preventDefault();
      const rect = boxRef.current.getBoundingClientRect();
      const startX = e.clientX - rect.left;
      const startY = e.clientY - rect.top;
      setBoxSelect({ startX, startY, endX: startX, endY: startY });
      if (prodMapRef.current) prodMapRef.current.setOptions({ draggable: false });
    };
    const handleBoxMouseMove = (e) => {
      if (!boxSelect) return;
      const rect = boxRef.current.getBoundingClientRect();
      setBoxSelect(prev => ({ ...prev, endX: e.clientX - rect.left, endY: e.clientY - rect.top }));
    };
    const handleBoxMouseUp = () => {
      if (!boxSelect) return;
      const { startX, startY, endX, endY } = boxSelect;
      // Only select if dragged a meaningful distance (>10px)
      if (Math.abs(endX - startX) > 10 && Math.abs(endY - startY) > 10) {
        finishBoxSelect(startX, startY, endX, endY);
      }
      setBoxSelect(null);
      if (prodMapRef.current) prodMapRef.current.setOptions({ draggable: true });
    };

    // Touch handlers for box select (mobile: boxMode toggle)
    const handleBoxTouchStart = (e) => {
      if (!boxMode) return;
      if (e.touches.length !== 1) return;
      const rect = boxRef.current.getBoundingClientRect();
      const startX = e.touches[0].clientX - rect.left;
      const startY = e.touches[0].clientY - rect.top;
      setBoxSelect({ startX, startY, endX: startX, endY: startY });
    };
    const handleBoxTouchMove = (e) => {
      if (!boxSelect || !boxMode) return;
      e.preventDefault();
      const rect = boxRef.current.getBoundingClientRect();
      setBoxSelect(prev => ({ ...prev, endX: e.touches[0].clientX - rect.left, endY: e.touches[0].clientY - rect.top }));
    };
    const handleBoxTouchEnd = () => {
      if (!boxSelect) return;
      const { startX, startY, endX, endY } = boxSelect;
      if (Math.abs(endX - startX) > 10 && Math.abs(endY - startY) > 10) {
        finishBoxSelect(startX, startY, endX, endY);
      }
      setBoxSelect(null);
    };

    // Filter toggle pill helper
    const statusFilters = [
      { value: 'Not Started', label: 'Not Started', color: '#9E9E9E' },
      { value: 'In Progress', label: 'In Progress', color: STATUS_COLORS.IN_PROGRESS },
      { value: 'Complete', label: 'Complete', color: STATUS_COLORS.COMPLETE },
      { value: 'Issue', label: 'Issue', color: '#FF5722' },
    ];
    const deviceFilters = [
      { value: 'handhole', label: 'Handholes', color: '#2196F3' },
      { value: 'flowerpot', label: 'Flowerpots', color: '#9C27B0' },
      { value: 'splice', label: 'Splice Points', color: '#E91E63' },
    ];
    const toggleStatus = (val) => {
      setHiddenStatuses(prev => {
        const next = new Set(prev);
        if (next.has(val)) next.delete(val); else next.add(val);
        return next;
      });
    };
    const toggleDeviceType = (val) => {
      setHiddenDeviceTypes(prev => {
        const next = new Set(prev);
        if (next.has(val)) next.delete(val); else next.add(val);
        return next;
      });
    };

    const renderFilterPills = () => (
      <>
        <div style={{ marginBottom: '8px' }}>
          <div style={{ fontSize: '0.7rem', color: tc.textMuted, marginBottom: '4px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Segments</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {statusFilters.map(f => {
              const visible = !hiddenStatuses.has(f.value);
              return (
                <button key={f.value} onClick={() => toggleStatus(f.value)} style={{
                  padding: '3px 8px', borderRadius: '6px', border: `1.5px solid ${f.color}`,
                  backgroundColor: visible ? f.color : 'transparent',
                  color: visible ? '#fff' : f.color,
                  fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer',
                  opacity: visible ? 1 : 0.5, transition: 'all 0.15s',
                }}>{f.label}</button>
              );
            })}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: tc.textMuted, marginBottom: '4px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Devices</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {deviceFilters.map(f => {
              const visible = !hiddenDeviceTypes.has(f.value);
              return (
                <button key={f.value} onClick={() => toggleDeviceType(f.value)} style={{
                  padding: '3px 8px', borderRadius: '6px', border: `1.5px solid ${f.color}`,
                  backgroundColor: visible ? f.color : 'transparent',
                  color: visible ? '#fff' : f.color,
                  fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer',
                  opacity: visible ? 1 : 0.5, transition: 'all 0.15s',
                }}>{f.label}</button>
              );
            })}
          </div>
        </div>
      </>
    );

    // Device marker icon helper for organize mode
    const getDeviceIcon = (device, isOrgSelected) => {
      const fill = isOrgSelected ? '#FFD700' : (
        device.device_type === 'handhole' ? '#2196F3' :
        device.device_type === 'flowerpot' ? '#9C27B0' : '#E91E63'
      );
      const shape = device.device_type === 'flowerpot' ? 'diamond' : 'circle';
      if (shape === 'diamond') {
        return {
          path: 'M 0,-8 L 6,0 L 0,8 L -6,0 Z',
          fillColor: fill, fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2, scale: 1.2,
        };
      }
      return {
        path: window.google.maps.SymbolPath.CIRCLE,
        fillColor: fill, fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2, scale: 7,
      };
    };

    const sectionDisplayName = isAllMode ? 'All Segments' : (selectedSection || 'Unsectioned');

    return (
      <div
        ref={boxRef}
        onMouseDown={handleBoxMouseDown}
        onMouseMove={handleBoxMouseMove}
        onMouseUp={handleBoxMouseUp}
        onMouseLeave={handleBoxMouseUp}
        onTouchStart={handleBoxTouchStart}
        onTouchMove={handleBoxTouchMove}
        onTouchEnd={handleBoxTouchEnd}
        style={{ position: 'relative', height: mapHeight, margin: isMobile ? '-16px' : '-32px', marginTop: isMobile ? '-8px' : '-32px' }}
      >
        {/* Box select rectangle overlay */}
        {boxSelect && (
          <div style={{
            position: 'absolute',
            left: Math.min(boxSelect.startX, boxSelect.endX),
            top: Math.min(boxSelect.startY, boxSelect.endY),
            width: Math.abs(boxSelect.endX - boxSelect.startX),
            height: Math.abs(boxSelect.endY - boxSelect.startY),
            border: '2px solid #FFD700',
            backgroundColor: 'rgba(255, 215, 0, 0.15)',
            zIndex: 15,
            pointerEvents: 'none',
            borderRadius: '2px',
          }} />
        )}

        {/* Back bar + section name */}
        <div style={{
          position: 'absolute', top: isMobile ? '8px' : '12px', left: isMobile ? '8px' : '12px',
          zIndex: 10, display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          <button onClick={() => { setSelectedSection(null); setSelectedSegIds(new Set()); setSelectedDevIds(new Set()); setMapMenuOpen(false); setBoxMode(false); setBoxSelect(null); setFilterPanelOpen(false); setHiddenStatuses(new Set()); setHiddenDeviceTypes(new Set()); setMapViewMode('all'); prodMapRef.current = null; }} style={{
            display: 'flex', alignItems: 'center', gap: '4px', padding: '8px 12px',
            backgroundColor: darkMode ? 'rgba(30,30,30,0.92)' : 'rgba(255,255,255,0.95)',
            borderRadius: '10px', boxShadow: '0 2px 12px rgba(0,0,0,0.2)', backdropFilter: 'blur(8px)',
            border: 'none', cursor: 'pointer', color: tc.text, fontWeight: 700, fontSize: '0.85rem',
          }}>
            <ChevronLeft size={16} />
            {sectionDisplayName}
          </button>
        </div>

        {/* Mobile: hamburger menu button */}
        {isMobile ? (
          <>
            <button onClick={() => setMapMenuOpen(!mapMenuOpen)} style={{
              position: 'absolute', top: '8px', right: '8px', zIndex: 11,
              width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: darkMode ? 'rgba(30,30,30,0.92)' : 'rgba(255,255,255,0.95)',
              borderRadius: '10px', boxShadow: '0 2px 12px rgba(0,0,0,0.2)', backdropFilter: 'blur(8px)',
              border: 'none', cursor: 'pointer', color: tc.text,
            }}>
              {mapMenuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>

            {/* Hamburger dropdown */}
            {mapMenuOpen && (
              <div style={{
                position: 'absolute', top: '56px', right: '8px', zIndex: 11,
                backgroundColor: darkMode ? 'rgba(30,30,30,0.97)' : 'rgba(255,255,255,0.98)',
                borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.25)', backdropFilter: 'blur(12px)',
                padding: '12px', minWidth: '180px',
              }}>
                {/* Rectangle Select toggle */}
                <div style={{ marginBottom: '10px' }}>
                  <button onClick={() => setDrawRectMode(prev => !prev)} style={{
                    width: '100%', padding: '10px 12px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '8px', minHeight: '48px',
                    backgroundColor: drawRectMode ? '#FFD700' : (darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'),
                    color: drawRectMode ? '#000' : tc.textMuted,
                    fontSize: '0.85rem', fontWeight: 600, transition: 'all 0.15s',
                  }}>
                    <Square size={16} />
                    Draw Select {drawRectMode ? 'ON' : 'OFF'}
                  </button>
                </div>

                {/* Quick Status toggle */}
                <div style={{ marginBottom: '10px' }}>
                  <button onClick={() => {
                    setQuickStatusOn(prev => {
                      const next = !prev;
                      try { localStorage.setItem('lyt-quick-status', String(next)); } catch {}
                      return next;
                    });
                  }} style={{
                    width: '100%', padding: '10px 12px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '8px', minHeight: '48px',
                    backgroundColor: quickStatusOn ? '#4CAF50' : (darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'),
                    color: quickStatusOn ? '#fff' : tc.textMuted,
                    fontSize: '0.85rem', fontWeight: 600, transition: 'all 0.15s',
                  }}>
                    <CheckCircle size={16} />
                    Quick Status {quickStatusOn ? 'ON' : 'OFF'}
                  </button>
                </div>

                {/* Add Note toggle */}
                <div style={{ marginBottom: '10px' }}>
                  <button onClick={() => { setIsAddingNote(prev => !prev); setMapMenuOpen(false); }} style={{
                    width: '100%', padding: '10px 12px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '8px', minHeight: '48px',
                    backgroundColor: isAddingNote ? '#FF9800' : (darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'),
                    color: isAddingNote ? '#fff' : tc.textMuted,
                    fontSize: '0.85rem', fontWeight: 600, transition: 'all 0.15s',
                  }}>
                    <MapPin size={16} />
                    Add Note {isAddingNote ? 'ON' : 'OFF'}
                  </button>
                </div>

                {/* View mode selector */}
                {viewModeOptions.length > 1 && (
                  <div style={{ borderTop: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`, paddingTop: '10px', marginBottom: '10px' }}>
                    <div style={{ fontSize: '0.7rem', color: tc.textMuted, marginBottom: '6px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>View</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                      {viewModeOptions.map(opt => {
                        const active = mapViewMode === opt.value;
                        return (
                          <button key={opt.value} onClick={() => setMapViewMode(opt.value)} style={{
                            padding: '6px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            backgroundColor: active ? (opt.color || tc.accentPrimary) : 'transparent',
                            color: active ? '#fff' : tc.text,
                            fontSize: '0.8rem', fontWeight: active ? 700 : 500, transition: 'all 0.15s',
                          }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              {opt.color && !active && <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: opt.color, display: 'inline-block' }} />}
                              {opt.label}
                            </span>
                            {opt.desc && <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>{opt.desc}</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Filters */}
                <div style={{ borderTop: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`, paddingTop: '10px', marginBottom: '10px' }}>
                  <div style={{ fontSize: '0.7rem', color: tc.textMuted, marginBottom: '6px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Filters</div>
                  {renderFilterPills()}
                </div>

                {/* Stats */}
                <div style={{ borderTop: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`, paddingTop: '10px' }}>
                  <div style={{ fontSize: '0.7rem', color: tc.textMuted, marginBottom: '6px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Stats</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.8rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: tc.textMuted }}>Total</span>
                      <span style={{ color: tc.text, fontWeight: 700 }}>{prodStats.total}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: tc.textMuted }}>Done</span>
                      <span style={{ color: STATUS_COLORS.COMPLETE, fontWeight: 700 }}>{prodStats.complete}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: tc.textMuted }}>Active</span>
                      <span style={{ color: STATUS_COLORS.IN_PROGRESS, fontWeight: 700 }}>{prodStats.inProgress}</span>
                    </div>
                    {prodStats.issues > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: tc.textMuted }}>Issues</span>
                        <span style={{ color: '#FF5722', fontWeight: 700 }}>{prodStats.issues}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          /* Desktop: inline stats bar */
          <div style={{
            position: 'absolute', top: '12px', left: '50%', transform: 'translateX(-50%)',
            zIndex: 10, display: 'flex', gap: '8px', padding: '8px 14px', alignItems: 'center',
            backgroundColor: darkMode ? 'rgba(20,20,22,0.92)' : 'rgba(255,255,255,0.95)',
            borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
            backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
            fontSize: '0.8rem', fontWeight: 600,
          }}>
            <span style={{ color: tc.text }}>{prodStats.total} total</span>
            <span style={{ color: '#9E9E9E' }}>|</span>
            <span style={{ color: STATUS_COLORS.COMPLETE }}>{prodStats.complete} done</span>
            <span style={{ color: '#9E9E9E' }}>|</span>
            <span style={{ color: STATUS_COLORS.IN_PROGRESS }}>{prodStats.inProgress} active</span>
            {prodStats.issues > 0 && <>
              <span style={{ color: '#9E9E9E' }}>|</span>
              <span style={{ color: '#FF5722' }}>{prodStats.issues} issues</span>
            </>}
            {orgSelCount > 0 && <>
              <span style={{ color: '#9E9E9E' }}>|</span>
              <span style={{ color: '#FFD700' }}>{orgSelCount} selected</span>
            </>}
            {/* View mode dropdown */}
            {viewModeOptions.length > 1 && (
              <>
                <span style={{ color: '#9E9E9E' }}>|</span>
                <select
                  value={mapViewMode}
                  onChange={(e) => setMapViewMode(e.target.value)}
                  style={{
                    padding: '3px 6px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600,
                    backgroundColor: mapViewMode !== 'all' ? `${tc.accentPrimary}20` : (darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'),
                    color: mapViewMode !== 'all' ? tc.accentPrimary : tc.text,
                    border: `1px solid ${mapViewMode !== 'all' ? tc.accentPrimary : 'transparent'}`,
                    cursor: 'pointer', outline: 'none',
                  }}
                >
                  {viewModeOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}{opt.desc ? ` (${opt.desc})` : ''}</option>
                  ))}
                </select>
              </>
            )}
            <span style={{ color: '#9E9E9E' }}>|</span>
            {/* Quick Status toggle */}
            <button onClick={() => {
              setQuickStatusOn(prev => {
                const next = !prev;
                try { localStorage.setItem('lyt-quick-status', String(next)); } catch {}
                return next;
              });
            }} style={{
              display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px',
              borderRadius: '6px', border: 'none', cursor: 'pointer',
              backgroundColor: quickStatusOn ? '#4CAF5020' : 'transparent',
              color: quickStatusOn ? '#4CAF50' : tc.textMuted,
              fontSize: '0.75rem', fontWeight: 600, transition: 'all 0.15s',
            }}>
              <CheckCircle size={13} />
              {quickStatusOn ? 'Quick' : 'Quick'}
            </button>
            <span style={{ color: '#9E9E9E' }}>|</span>
            {/* Filter button */}
            <div style={{ position: 'relative' }}>
              <button onClick={() => setFilterPanelOpen(prev => !prev)} style={{
                display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px',
                borderRadius: '6px', border: 'none', cursor: 'pointer',
                backgroundColor: (hiddenStatuses.size > 0 || hiddenDeviceTypes.size > 0) ? `${tc.accentPrimary}20` : 'transparent',
                color: (hiddenStatuses.size > 0 || hiddenDeviceTypes.size > 0) ? tc.accentPrimary : tc.textMuted,
                fontSize: '0.75rem', fontWeight: 600, transition: 'all 0.15s',
              }}>
                <Filter size={13} />
                {(hiddenStatuses.size > 0 || hiddenDeviceTypes.size > 0) ? `${hiddenStatuses.size + hiddenDeviceTypes.size} hidden` : 'Filter'}
              </button>
              {filterPanelOpen && (
                <div style={{
                  position: 'absolute', top: '100%', right: 0, marginTop: '8px',
                  backgroundColor: darkMode ? 'rgba(30,30,30,0.97)' : 'rgba(255,255,255,0.98)',
                  borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.25)', backdropFilter: 'blur(12px)',
                  padding: '12px', minWidth: '220px', zIndex: 20,
                }}>
                  {renderFilterPills()}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sub color legend (all-subs or single sub mode) */}
        {(mapViewMode === 'all-subs' || (mapViewMode !== 'all' && mapViewMode !== 'lyt-only' && mapViewMode.includes('@'))) && subEntries.length > 0 && (
          <div style={{
            position: 'absolute', bottom: (selectedSegIds.size > 0 || selectedDevIds.size > 0) ? '200px' : (isMobile ? '20px' : '16px'),
            left: isMobile ? '8px' : '12px',
            zIndex: 10, padding: '10px 14px',
            backgroundColor: darkMode ? 'rgba(30,30,30,0.92)' : 'rgba(255,255,255,0.95)',
            borderRadius: '10px', boxShadow: '0 2px 12px rgba(0,0,0,0.2)', backdropFilter: 'blur(8px)',
          }}>
            <div style={{ fontSize: '0.7rem', color: tc.textMuted, marginBottom: '6px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {mapViewMode === 'all-subs' ? 'Subs' : 'Viewing'}
            </div>
            {(mapViewMode === 'all-subs' ? subEntries : subEntries.filter(s => s.email === mapViewMode)).map(sub => (
              <div key={sub.email} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
                <span style={{ width: 12, height: 4, borderRadius: '2px', backgroundColor: sub.color, display: 'inline-block' }} />
                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: tc.text }}>{sub.name}</span>
                <span style={{ fontSize: '0.7rem', color: tc.textMuted }}>{sub.segmentIds.size} segs</span>
              </div>
            ))}
          </div>
        )}

        {/* Map */}
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={prodMapRef.current ? undefined : (mappableSegs.length > 0 ? {
            lat: parseFloat(mappableSegs[0].gps_start?.lat ?? mappableSegs[0].gps_start_lat),
            lng: parseFloat(mappableSegs[0].gps_start?.lng ?? mappableSegs[0].gps_start_lng),
          } : { lat: 30.1, lng: -97.3 })}
          zoom={prodMapRef.current ? undefined : 14}
          onLoad={onMapLoad}
          onClick={(e) => { if (isAddingNote && MapNotesLayer.handleMapClick) MapNotesLayer.handleMapClick(e); }}
          mapTypeId={(() => { try { return localStorage.getItem('lyt-map-type') || 'roadmap'; } catch { return 'roadmap'; } })()}
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
            zoomControl: !isMobile,
            gestureHandling: 'greedy',
            draggable: true,
            styles: darkMode ? [
              { elementType: 'geometry', stylers: [{ color: '#212121' }] },
              { elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
              { elementType: 'labels.text.stroke', stylers: [{ color: '#212121' }] },
              { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2c2c2c' }] },
              { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#000000' }] },
              { featureType: 'poi', stylers: [{ visibility: 'off' }] },
              { featureType: 'transit', stylers: [{ visibility: 'off' }] },
            ] : [
              { featureType: 'poi', stylers: [{ visibility: 'off' }] },
              { featureType: 'transit', stylers: [{ visibility: 'off' }] },
            ],
          }}
        >
          {/* Segment polylines (filtered by visibility) */}
          {visibleSegs.map(seg => {
            const isSelected = selectedSegIds.has(seg.segment_id);
            const path = [
              { lat: parseFloat(seg.gps_start?.lat ?? seg.gps_start_lat), lng: parseFloat(seg.gps_start?.lng ?? seg.gps_start_lng) },
              { lat: parseFloat(seg.gps_end?.lat ?? seg.gps_end_lat), lng: parseFloat(seg.gps_end?.lng ?? seg.gps_end_lng) },
            ];
            return (
              <Polyline
                key={seg.segment_id}
                path={path}
                options={{
                  strokeColor: getSegColor(seg, isSelected),
                  strokeWeight: isSelected ? 8 : 5,
                  strokeOpacity: isSelected ? 1 : 0.85,
                  zIndex: isSelected ? 10 : 1,
                }}
                onClick={() => {
                  setSelectedSegIds(prev => {
                    const next = new Set(prev);
                    if (next.has(seg.segment_id)) next.delete(seg.segment_id);
                    else next.add(seg.segment_id);
                    return next;
                  });
                }}
              />
            );
          })}

          {/* Device markers — always visible */}
          {visibleDevices.map(dev => {
            const isOrgSelected = selectedDevIds.has(dev.device_id);
            return (
              <Marker
                key={dev.device_id}
                position={{ lat: parseFloat(dev.gps_lat), lng: parseFloat(dev.gps_lng) }}
                icon={getDeviceIcon(dev, isOrgSelected)}
                onClick={() => {
                  setSelectedDevIds(prev => {
                    const next = new Set(prev);
                    if (next.has(dev.device_id)) next.delete(dev.device_id);
                    else next.add(dev.device_id);
                    return next;
                  });
                }}
              />
            );
          })}

          {/* Map Notes */}
          {showNotes && (
            <MapNotesLayer
              notes={mapNotes}
              darkMode={darkMode}
              loggedInUser={loggedInUser}
              projectId={mySegments[0]?.project_id || loggedInUser?.project_id || 'BASTROP'}
              section={null}
              onNotesChanged={async () => {
                try {
                  const notes = await loadMapNotes(mySegments[0]?.project_id || loggedInUser?.project_id || 'BASTROP');
                  setMapNotes(notes || []);
                } catch (e) { /* noop */ }
              }}
              isAddingNote={isAddingNote}
              onAddNoteToggle={setIsAddingNote}
              segments={mappableSegs || []}
              devices={visibleDevices || []}
              isAdmin={false}
              isMobile={isMobile}
            />
          )}
          {/* Rectangle drawing tool */}
          {drawRectMode && (
            <DrawingManager
              drawingMode="rectangle"
              onRectangleComplete={onRectangleComplete}
              options={{
                drawingControl: false,
                rectangleOptions: {
                  fillColor: '#FFD700', fillOpacity: 0.15,
                  strokeColor: '#FFD700', strokeWeight: 2,
                  editable: false, draggable: false,
                },
              }}
            />
          )}
          {/* GPS blue dot */}
          {gpsOn && loggedInUser && (
            <MyLocationMarker
              loggedInUser={loggedInUser}
              segments={mappableSegs || []}
              projectId={mySegments[0]?.project_id || loggedInUser?.project_id || 'BASTROP'}
            />
          )}
        </GoogleMap>

        {/* GPS toggle button */}
        {loggedInUser && <GpsToggleButton darkMode={darkMode} mapRef={prodMapRef.current} gpsOn={gpsOn} onToggle={setGpsOn} />}

        {/* Quick Status floating buttons (when toggle is ON) */}
        <QuickStatusButtons
          darkMode={darkMode}
          visible={quickStatusOn}
          selectedCount={selectedSegIds.size + selectedDevIds.size}
          onStatusUpdate={handleStatusUpdate}
          updating={statusUpdating}
        />

        {/* Unified MapActionBar — single or batch, with collapsible section assignment */}
        <MapActionBar
          darkMode={darkMode}
          selectedSegment={selectedSegIds.size === 1 ? sectionSegs.find(s => selectedSegIds.has(s.segment_id)) : null}
          selectedCount={selectedSegIds.size > 1 ? selectedSegIds.size + selectedDevIds.size : (selectedDevIds.size > 0 ? selectedDevIds.size : 0)}
          statusField={statusField}
          onStatusUpdate={handleStatusUpdate}
          onClear={() => { setSelectedSegIds(new Set()); setSelectedDevIds(new Set()); }}
          updating={statusUpdating}
        >
          {/* Collapsible section assignment */}
          {(selectedSegIds.size > 0 || selectedDevIds.size > 0) && (
            <div style={{ marginBottom: '16px' }}>
              <button onClick={() => setShowSectionAssign(!showSectionAssign)} style={{
                background: 'none', border: `1px solid ${darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'}`,
                borderRadius: '10px', padding: '10px 14px', width: '100%',
                color: darkMode ? 'rgba(255,255,255,0.55)' : '#6b7280',
                fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                minHeight: '44px',
              }}>
                <span>Assign to Section</span>
                <ChevronRight size={16} style={{ transform: showSectionAssign ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />
              </button>
              {showSectionAssign && (
                <div style={{ marginTop: '10px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {existingSections.filter(s => s.name !== '').length > 0 && (
                    <select
                      value={sectionInput}
                      onChange={(e) => setSectionInput(e.target.value)}
                      style={{
                        flex: '1 1 140px', minWidth: 0, height: '48px', borderRadius: '10px',
                        border: `1px solid ${darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'}`,
                        backgroundColor: darkMode ? 'rgba(255,255,255,0.06)' : '#f9fafb',
                        color: darkMode ? '#fff' : '#1e293b', fontSize: '0.9rem', padding: '0 12px',
                      }}
                    >
                      <option value="">Existing section...</option>
                      {existingSections.filter(s => s.name !== '').map(s => (
                        <option key={s.name} value={s.name}>{s.name} ({s.segments}S / {s.devices}D)</option>
                      ))}
                    </select>
                  )}
                  <input
                    type="text" value={sectionInput}
                    onChange={(e) => setSectionInput(e.target.value)}
                    placeholder="New section..."
                    style={{
                      flex: '1 1 140px', minWidth: 0, height: '48px', borderRadius: '10px',
                      border: `1px solid ${darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'}`,
                      backgroundColor: darkMode ? 'rgba(255,255,255,0.06)' : '#f9fafb',
                      color: darkMode ? '#fff' : '#1e293b', fontSize: '0.9rem', padding: '0 12px',
                    }}
                  />
                  <button
                    disabled={!sectionInput.trim() || assigningSection}
                    onClick={() => handleAssignSection(sectionInput)}
                    style={{
                      height: '48px', padding: '0 20px', borderRadius: '10px', border: 'none',
                      background: sectionInput.trim() ? '#FFD700' : (darkMode ? '#333' : '#e5e7eb'),
                      color: sectionInput.trim() ? '#000' : (darkMode ? '#888' : '#999'),
                      fontWeight: 700, cursor: sectionInput.trim() && !assigningSection ? 'pointer' : 'default',
                      fontSize: '0.85rem', opacity: assigningSection ? 0.6 : 1,
                      display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap',
                    }}
                  >
                    {assigningSection ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : null}
                    {assigningSection ? 'Assigning...' : 'Assign'}
                  </button>
                </div>
              )}
            </div>
          )}
        </MapActionBar>
      </div>
    );
  };

  const renderEquipment = () => (
    <div>
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '8px', color: tc.text }}>Equipment Inspection</h2>
        <p style={{ color: tc.textMuted }}>Pre-use vehicle and equipment check</p>
      </div>

      <div style={{ ...cardStyle, maxWidth: '600px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: tc.text }}>Vehicle/Equipment ID *</label>
            <input type="text" value={equipmentForm.vehicleId} onChange={(e) => setEquipmentForm({...equipmentForm, vehicleId: e.target.value})} placeholder="e.g., T-101" style={formInputStyle('eq-vid')} {...inputFocusProps('eq-vid')} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: tc.text }}>Mileage/Hours</label>
            <input type="text" value={equipmentForm.mileage} onChange={(e) => setEquipmentForm({...equipmentForm, mileage: e.target.value})} placeholder="Current reading" style={formInputStyle('eq-mil')} {...inputFocusProps('eq-mil')} />
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: tc.text }}>Safety Checklist</label>
          {['Lights & Signals', 'Tires & Brakes', 'Fluid Levels', 'Safety Equipment', 'Clean Interior'].map((item) => (
            <label key={item} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', color: tc.text, cursor: 'pointer', padding: '8px 12px', borderRadius: '8px', background: equipmentForm.safetyItems.includes(item) ? `${tc.success}10` : 'transparent', transition: 'background 0.2s' }}>
              <input type="checkbox" checked={equipmentForm.safetyItems.includes(item)} onChange={(e) => {
                if (e.target.checked) {
                  setEquipmentForm({...equipmentForm, safetyItems: [...equipmentForm.safetyItems, item]});
                } else {
                  setEquipmentForm({...equipmentForm, safetyItems: equipmentForm.safetyItems.filter(i => i !== item)});
                }
              }} />
              {item}
            </label>
          ))}
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: tc.text }}>Issues Found</label>
          <textarea value={equipmentForm.issues} onChange={(e) => setEquipmentForm({...equipmentForm, issues: e.target.value})} rows={3} placeholder="Describe any issues found..." style={{ ...formInputStyle('eq-issues'), resize: 'vertical' }} {...inputFocusProps('eq-issues')} />
        </div>

        <button onClick={handleEquipmentSubmit} disabled={loading} style={{
          ...getButtonStyle('secondary', darkMode), width: '100%', height: '48px', fontSize: '1rem',
          cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}>
          {loading ? <Loader size={18} /> : <CheckCircle size={18} />}
          {loading ? 'Submitting...' : 'Submit Inspection'}
        </button>
      </div>
    </div>
  );

  const renderIncidents = () => (
    <div>
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '8px', color: tc.text }}>Incident Report</h2>
        <p style={{ color: tc.textMuted }}>Report safety incidents, near-misses, or property damage</p>
      </div>

      <div style={{ ...cardStyle, maxWidth: '600px' }}>
        <div style={{ backgroundColor: `${tc.error}15`, padding: '12px', borderRadius: '8px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertTriangle size={20} color={tc.error} />
          <span style={{ color: tc.error, fontSize: '0.9rem' }}>For emergencies, call 911 first!</span>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: tc.text }}>Incident Type *</label>
          <select value={incidentForm.type} onChange={(e) => setIncidentForm({...incidentForm, type: e.target.value})} style={formSelectStyle('inc-type')} {...inputFocusProps('inc-type')}>
            <option value="">Select Type</option>
            <option value="Near Miss">Near Miss</option>
            <option value="Property Damage">Property Damage</option>
            <option value="Vehicle Accident">Vehicle Accident</option>
            <option value="Injury">Injury</option>
            <option value="Utility Strike">Utility Strike</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: tc.text }}>Project/Location</label>
          <select value={incidentForm.project} onChange={(e) => setIncidentForm({...incidentForm, project: e.target.value})} style={formSelectStyle('inc-proj')} {...inputFocusProps('inc-proj')}>
            <option value="">Select Project</option>
            {projectOptions.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: tc.text }}>Description *</label>
          <textarea value={incidentForm.description} onChange={(e) => setIncidentForm({...incidentForm, description: e.target.value})} rows={4} placeholder="Describe what happened..." style={{ ...formInputStyle('inc-desc'), resize: 'vertical' }} {...inputFocusProps('inc-desc')} />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: tc.text, cursor: 'pointer' }}>
            <input type="checkbox" checked={incidentForm.injuries} onChange={(e) => setIncidentForm({...incidentForm, injuries: e.target.checked})} />
            Were there any injuries?
          </label>
        </div>

        <button onClick={handleIncidentSubmit} disabled={loading} style={{
          ...getButtonStyle('danger', darkMode), width: '100%', height: '48px', fontSize: '1rem',
          cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}>
          {loading ? <Loader size={18} /> : <ShieldAlert size={18} />}
          {loading ? 'Submitting...' : 'Submit Incident Report'}
        </button>
      </div>
    </div>
  );

  const renderCompliance = () => {
    return (
      <ComplianceHub
        darkMode={darkMode}
        loggedInUser={loggedInUser}
        complianceItems={complianceItems}
        complianceLoading={complianceLoading}
        loadComplianceData={loadComplianceData}
      />
    );
  };

  const renderInvoices = () => {
    if (myInvoices.length === 0 && !invoicesLoading) loadMyInvoices();

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '8px', color: tc.text }}>Invoices</h2>
            <p style={{ color: tc.textMuted }}>Track and manage your submitted invoices</p>
          </div>
          <button onClick={() => setCurrentPage('contractor-invoice')} style={{
            ...getButtonStyle('primary', darkMode), background: tc.gradientPortal, height: '44px',
            display: 'flex', alignItems: 'center', gap: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}>
            <Plus size={16} /> Create Invoice
          </button>
        </div>

        {/* Summary Cards — gradient top border */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '24px' }}>
          {[
            { label: 'Total Submitted', value: invoicesLoading ? null : String(invoiceStats.total), color: '#2196F3' },
            { label: 'Approved', value: invoicesLoading ? null : String(invoiceStats.approved), color: '#4CAF50' },
            { label: 'Paid', value: invoicesLoading ? null : String(invoiceStats.paid), color: '#00b4d8' },
          ].map((s, i) => (
            <div key={i} style={{ ...cardStyle, padding: '16px', textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
              {s.value === null ? (
                <Skeleton width="40px" height="28px" style={{ margin: '0 auto 6px' }} />
              ) : (
                <div style={{ fontSize: '1.75rem', fontWeight: '700', color: s.color }}>{s.value}</div>
              )}
              <div style={{ fontSize: '0.8rem', color: tc.textMuted }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Invoice Table */}
        {invoicesLoading ? (
          <div style={cardStyle}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[1,2,3].map(i => (
                <div key={i} style={{ display: 'flex', gap: '16px', padding: '12px 0', borderBottom: `1px solid ${tc.border}` }}>
                  <Skeleton width="80px" height="16px" />
                  <Skeleton width="100px" height="16px" />
                  <Skeleton width="80px" height="16px" />
                  <Skeleton width="60px" height="22px" style={{ borderRadius: '20px' }} />
                </div>
              ))}
            </div>
          </div>
        ) : myInvoices.length === 0 ? (
          <div style={{ ...cardStyle, textAlign: 'center', padding: '48px 24px' }}>
            <FileText size={56} color={tc.textMuted} style={{ marginBottom: '16px', opacity: 0.3 }} />
            <h3 style={{ color: tc.text, marginBottom: '8px' }}>No invoices yet</h3>
            <p style={{ color: tc.textMuted, marginBottom: '20px', fontSize: '0.9rem' }}>Create your first invoice to start tracking payments.</p>
            <button onClick={() => setCurrentPage('contractor-invoice')} style={{
              ...getButtonStyle('primary', darkMode), background: tc.gradientPortal,
            }}>
              Create Invoice
            </button>
          </div>
        ) : (
          <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', minWidth: 600 }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${tc.border}` }}>
                    {['Invoice #', 'Date', 'Amount', 'Retainage', 'Net', 'Status'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '14px 16px', color: tc.textMuted, fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {myInvoices.map((inv, idx) => (
                    <tr key={idx}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = idx % 2 === 1 ? (darkMode ? 'rgba(255,255,255,0.02)' : '#f9fafb') : 'transparent'}
                      style={{
                        borderBottom: `1px solid ${tc.borderSubtle}`,
                        backgroundColor: idx % 2 === 1 ? (darkMode ? 'rgba(255,255,255,0.02)' : '#f9fafb') : 'transparent',
                        transition: 'background-color 0.15s',
                      }}>
                      <td style={{ padding: '12px 16px', fontFamily: 'monospace', fontWeight: 600, color: tc.accentPrimary }}>{inv.invoice_number}</td>
                      <td style={{ padding: '12px 16px', color: tc.text }}>{inv.submitted_at ? new Date(inv.submitted_at).toLocaleDateString() : inv.created_at ? new Date(inv.created_at).toLocaleDateString() : '—'}</td>
                      <td style={{ padding: '12px 16px', fontWeight: 500, color: tc.text }}>${(parseFloat(inv.subtotal) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                      <td style={{ padding: '12px 16px', color: tc.textMuted }}>${(parseFloat(inv.retainage_amount) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                      <td style={{ padding: '12px 16px', fontWeight: 600, color: tc.text }}>${(parseFloat(inv.net_amount) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                      <td style={{ padding: '12px 16px' }}>{statusBadge(inv.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderBilling = () => {
    // Load on first view
    if (!lineItemsLoading && myLineItems.length === 0 && mySegments.length > 0) {
      loadMyLineItems();
    }
    // Also load invoices for summary
    if (myInvoices.length === 0 && !invoicesLoading) {
      loadMyInvoices();
    }

    const totalValue = myLineItems.reduce((sum, li) => {
      const qty = parseFloat(li.quantity) || 0;
      const rate = parseFloat(li.contractor_rate) || 0;
      return sum + (qty * rate);
    }, 0);

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '8px', color: tc.text }}>My Billing</h2>
            <p style={{ color: tc.textMuted }}>Line items, rates, and payment summary for your assigned work</p>
          </div>
          <button onClick={() => setActiveTab('invoices')} style={{
            ...getButtonStyle('outline', darkMode), display: 'flex', alignItems: 'center', gap: '6px',
          }}>
            <FileText size={16} /> View Invoices
          </button>
        </div>

        {/* Payment Summary Row — gradient top borders */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: '12px', marginBottom: '24px' }}>
          {[
            { label: 'Total Billable', value: `$${totalValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, color: tc.accentPrimary },
            { label: 'Invoiced', value: `$${invoiceStats.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, color: '#2196F3' },
            { label: 'Retainage Held', value: `$${invoiceStats.totalRetainage.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, color: '#FFB800' },
            { label: 'Net Received', value: `$${invoiceStats.totalPaid.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, color: tc.accentSecondary },
          ].map((s, i) => (
            <div key={i} style={{ ...cardStyle, padding: '16px', borderTop: `3px solid ${s.color}` }}>
              <div style={{ fontSize: '0.8rem', color: tc.textMuted, marginBottom: '4px' }}>{s.label}</div>
              <div style={{ fontSize: '1.25rem', fontWeight: '700', color: s.color }}>{s.value}</div>
            </div>
          ))}
        </div>

        {lineItemsLoading ? (
          <div style={cardStyle}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[1,2,3,4].map(i => (
                <div key={i} style={{ display: 'flex', gap: '16px', padding: '10px 0', borderBottom: `1px solid ${tc.border}` }}>
                  <Skeleton width="60px" height="14px" />
                  <Skeleton width="200px" height="14px" />
                  <Skeleton width="40px" height="14px" />
                  <Skeleton width="60px" height="14px" />
                  <Skeleton width="80px" height="14px" />
                </div>
              ))}
            </div>
          </div>
        ) : myLineItems.length === 0 ? (
          <div style={{ ...cardStyle, textAlign: 'center', padding: '48px 24px' }}>
            <DollarSign size={56} color={tc.textMuted} style={{ marginBottom: '16px', opacity: 0.3 }} />
            <div style={{ color: tc.text, fontWeight: 500, marginBottom: '4px' }}>No billing data available yet</div>
            <div style={{ fontSize: '0.85rem', color: tc.textMuted }}>Billing data appears after work orders are imported</div>
          </div>
        ) : (
          <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', minWidth: 500 }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${tc.border}` }}>
                    {['Code', 'Description', 'UOM', 'Qty', 'Rate', 'Total'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '12px', color: tc.textMuted, fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {myLineItems.map((li, idx) => {
                    const qty = parseFloat(li.quantity) || 0;
                    const rate = parseFloat(li.contractor_rate) || 0;
                    const total = qty * rate;
                    return (
                      <tr key={idx}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = idx % 2 === 1 ? (darkMode ? 'rgba(255,255,255,0.02)' : '#f9fafb') : 'transparent'}
                        style={{
                          borderBottom: `1px solid ${tc.borderSubtle}`,
                          backgroundColor: idx % 2 === 1 ? (darkMode ? 'rgba(255,255,255,0.02)' : '#f9fafb') : 'transparent',
                          transition: 'background-color 0.15s',
                        }}>
                        <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontWeight: 600, color: tc.accentPrimary }}>{li.code}</td>
                        <td style={{ padding: '10px 12px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: tc.text }}>{li.description}</td>
                        <td style={{ padding: '10px 12px', color: tc.textMuted }}>{li.uom}</td>
                        <td style={{ padding: '10px 12px', fontWeight: 500, color: tc.text }}>{qty.toLocaleString()}</td>
                        <td style={{ padding: '10px 12px', color: tc.text }}>${rate.toFixed(2)}</td>
                        <td style={{ padding: '10px 12px', fontWeight: 600, color: tc.text }}>${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ backgroundColor: tc.topBarBg }}>
                    <td colSpan={5} style={{ padding: '12px', fontWeight: 700, textAlign: 'right', color: tc.text }}>Total:</td>
                    <td style={{ padding: '12px', fontWeight: 700, color: tc.accentSecondary }}>
                      ${totalValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderAssignments = () => {
    return (
      <div>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: tc.text, marginBottom: '8px' }}>Work Assignments</h2>
        <p style={{ color: tc.textMuted, marginBottom: '24px' }}>Assignments from management. Accept work, then optionally assign to your subs.</p>
        {assignmentsLoading ? (
          <div style={cardStyle}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[1,2,3].map(i => (
                <div key={i} style={{ padding: '16px', backgroundColor: tc.cardBgAlt, borderRadius: '8px' }}>
                  <Skeleton width="60%" height="16px" style={{ marginBottom: '8px' }} />
                  <Skeleton width="80%" height="12px" style={{ marginBottom: '6px' }} />
                  <Skeleton width="40%" height="12px" />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <AssignmentManager
            assignments={myAssignments}
            subs={mySubs}
            darkMode={darkMode}
            user={loggedInUser}
            onAccept={async (id) => {
              const a = myAssignments.find(x => x.assignment_id === id);
              await acceptAssignment(id, {
                contractorName: loggedInUser?.company || loggedInUser?.name,
                workType: a?.work_type,
                segmentCount: JSON.parse(a?.segment_ids || '[]').length,
              });
              await loadMyAssignments();
            }}
            onDecline={async (id, reason) => {
              const a = myAssignments.find(x => x.assignment_id === id);
              await declineAssignment(id, reason, {
                contractorName: loggedInUser?.company || loggedInUser?.name,
                workType: a?.work_type,
              });
              await loadMyAssignments();
            }}
            onAssignToSub={async (id, subEmail) => {
              await assignToSub(id, subEmail);
              await loadMyAssignments();
            }}
          />
        )}
      </div>
    );
  };

  // Load subs when team tab is selected
  useEffect(() => {
    if (activeTab === 'team' && !subsLoadedRef.current) {
      subsLoadedRef.current = true;
      loadMySubs();
    }
  }, [activeTab, loadMySubs]);

  const renderTeam = () => {
    return (
      <div>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: tc.text, marginBottom: '8px' }}>My Team</h2>
        <p style={{ color: tc.textMuted, marginBottom: '24px' }}>Sub-contractors working under your company.</p>
        {subsLoading ? (
          <div style={{ display: 'grid', gap: '12px' }}>
            {[1,2].map(i => (
              <div key={i} style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: '16px' }}>
                <Skeleton width="48px" height="48px" style={{ borderRadius: '50%', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <Skeleton width="50%" height="16px" style={{ marginBottom: '6px' }} />
                  <Skeleton width="70%" height="12px" />
                </div>
                <Skeleton width="60px" height="24px" style={{ borderRadius: '20px' }} />
              </div>
            ))}
          </div>
        ) : mySubs.length === 0 ? (
          <div style={{ ...cardStyle, textAlign: 'center', padding: '48px 24px' }}>
            <Users size={56} color={tc.textMuted} style={{ marginBottom: '16px', opacity: 0.3 }} />
            <p style={{ color: tc.text, fontWeight: 500, marginBottom: '4px' }}>No sub-contractors on your team yet</p>
            <p style={{ color: tc.textMuted, fontSize: '0.85rem' }}>Go to Settings to invite your first sub-contractor.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '12px' }}>
            {mySubs.map((sub, idx) => (
              <div key={idx}>
                <div onMouseEnter={() => setHoveredCard(`sub-${idx}`)} onMouseLeave={() => setHoveredCard(null)}
                  style={{ ...hoverCardStyle(`sub-${idx}`), padding: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: tc.gradientPortal, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: '700', fontSize: '1.1rem', flexShrink: 0 }}>
                    {sub.name?.charAt(0) || '?'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', color: tc.text }}>{sub.name || 'Unknown'}</div>
                    <div style={{ fontSize: '0.85rem', color: tc.textMuted }}>{sub.email}</div>
                    {sub.company && <div style={{ fontSize: '0.8rem', color: tc.textMuted }}>{sub.company}</div>}
                  </div>
                  <button onClick={(e) => {
                    e.stopPropagation();
                    setAssignSubOpen(prev => prev === sub.email ? null : sub.email);
                    setSelectedSubSections(new Set());
                  }} style={{
                    padding: '6px 14px', borderRadius: '8px', border: `1px solid ${tc.accentPrimary}40`,
                    background: assignSubOpen === sub.email ? `${tc.accentPrimary}15` : 'transparent',
                    color: tc.accentPrimary, fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer',
                    whiteSpace: 'nowrap', marginRight: '8px',
                  }}>
                    {assignSubOpen === sub.email ? 'Cancel' : 'Assign Work'}
                  </button>
                  <span style={{
                    padding: '4px 12px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 700,
                    backgroundColor: sub.status === 'active' ? '#4CAF5015' : '#FFB80015',
                    color: sub.status === 'active' ? '#4CAF50' : '#FFB800',
                  }}>{sub.status || 'pending'}</span>
                </div>

                {/* Expandable section picker panel */}
                {assignSubOpen === sub.email && (
                  <div style={{
                    ...tCard, marginTop: '-4px', borderTopLeftRadius: 0, borderTopRightRadius: 0,
                    padding: '16px 20px', borderTop: `1px dashed ${tc.border}`,
                  }}>
                    {existingSections.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '20px 0' }}>
                        <MapPin size={32} color={tc.textMuted} style={{ marginBottom: '8px', opacity: 0.4 }} />
                        <p style={{ color: tc.textMuted, fontSize: '0.85rem', margin: 0 }}>
                          No sections yet. Go to <strong>Production</strong> tab, switch to <strong>Organize</strong> mode, and assign segments to sections first.
                        </p>
                      </div>
                    ) : (
                      <>
                        <div style={{ fontSize: '0.85rem', fontWeight: 600, color: tc.text, marginBottom: '12px' }}>
                          Select sections to assign to {sub.name}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                          {existingSections.map(sec => {
                            const isChecked = selectedSubSections.has(sec.name);
                            return (
                              <label key={sec.name} style={{
                                display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px',
                                borderRadius: '8px', cursor: 'pointer',
                                background: isChecked ? `${tc.accentPrimary}10` : tc.cardBgAlt,
                                border: `1px solid ${isChecked ? tc.accentPrimary + '40' : tc.borderLight}`,
                                transition: 'all 0.15s',
                              }}>
                                <input type="checkbox" checked={isChecked} onChange={() => {
                                  setSelectedSubSections(prev => {
                                    const next = new Set(prev);
                                    if (next.has(sec.name)) next.delete(sec.name);
                                    else next.add(sec.name);
                                    return next;
                                  });
                                }} />
                                <div style={{ flex: 1 }}>
                                  <span style={{ fontWeight: 600, color: tc.text }}>{sec.name}</span>
                                  <span style={{ color: tc.textMuted, fontSize: '0.8rem', marginLeft: '8px' }}>
                                    {sec.segments}S / {sec.devices}D
                                  </span>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                        <button
                          disabled={selectedSubSections.size === 0 || assigningToSub}
                          onClick={() => handleAssignToSub(sub)}
                          style={{
                            width: '100%', padding: '12px', borderRadius: '10px', border: 'none',
                            background: selectedSubSections.size > 0 ? tc.gradientPortal : (darkMode ? '#333' : '#e5e7eb'),
                            color: selectedSubSections.size > 0 ? '#fff' : tc.textMuted,
                            fontWeight: 700, fontSize: '0.85rem',
                            cursor: selectedSubSections.size > 0 && !assigningToSub ? 'pointer' : 'default',
                            opacity: assigningToSub ? 0.6 : 1,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                          }}
                        >
                          {assigningToSub ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : null}
                          {assigningToSub ? 'Assigning...' : `Assign ${selectedSubSections.size} section${selectedSubSections.size !== 1 ? 's' : ''} to ${sub.name}`}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderRates = () => {
    const projectIds = [...new Set(myAssignments.map(a => a.project_id).filter(Boolean))];
    const rateData = [];

    try {
      const { getClientConfig } = require('../config/constants');
      const { getRateInfo } = require('../config/mapConfig');

      for (const pid of projectIds) {
        try {
          let client = null;
          if (pid.toUpperCase().includes('BASTROP') || pid.toUpperCase().includes('HIGHLINE') || pid.toUpperCase().includes('ASPEN')) {
            client = 'Highline';
          }

          if (client) {
            const config = getClientConfig(client);
            if (config?.rateCard) {
              const info = getRateInfo(config.rateCard);
              if (info?.rates) {
                rateData.push({ projectId: pid, client, rates: info.rates, priceField: info.priceField || 'contractor_rate' });
              }
            }
          }
        } catch (e) {
          // Skip this project
        }
      }
    } catch (e) {
      // Rate resolution not available
    }

    const groupRates = (rates, priceField) => {
      const groups = {};
      const rateKeys = Object.keys(rates);
      for (const key of rateKeys) {
        const rate = rates[key];
        const prefix = key.replace(/\d+$/, '').replace(/-$/, '');
        const category = prefix === 'HL-UG' || prefix === 'UG' ? 'Underground'
          : prefix === 'HL-SPL' || prefix === 'SPL' ? 'Splicing'
          : prefix === 'HL-AER' || prefix === 'AER' ? 'Aerial'
          : prefix === 'HL-FS' || prefix === 'FS' ? 'Fiber Splicing'
          : 'Other';
        if (!groups[category]) groups[category] = [];
        // Contractor rates only — never fall back to client rate fields
        const price = rate[priceField] || rate.contractor_rate || 0;
        groups[category].push({
          code: key,
          description: rate.description || rate.desc || key,
          uom: rate.uom || rate.unit || 'EA',
          price: typeof price === 'number' ? price : parseFloat(price) || 0,
        });
      }
      return groups;
    };

    const exportCSV = (groups, projectId) => {
      const headers = ['Code', 'Description', 'UOM', 'Rate'];
      const rows = [];
      for (const [, items] of Object.entries(groups)) {
        for (const item of items) {
          rows.push([item.code, item.description, item.uom, item.price.toFixed(2)]);
        }
      }
      const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${projectId}_rate_card.csv`;
      a.click();
      URL.revokeObjectURL(url);
    };

    return (
      <div>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: tc.text, marginBottom: '8px' }}>My Rate Card</h2>
        <p style={{ color: tc.textMuted, marginBottom: '24px' }}>Rates for your assigned projects.</p>

        {rateData.length === 0 ? (
          <div style={{ ...cardStyle, textAlign: 'center', padding: '48px 24px' }}>
            <DollarSign size={56} color={tc.textMuted} style={{ marginBottom: 16, opacity: 0.3 }} />
            <p style={{ color: tc.text, fontWeight: 500, marginBottom: '4px' }}>No rate cards available</p>
            <p style={{ color: tc.textMuted, fontSize: '0.85rem' }}>Rates will appear here once you have project assignments.</p>
          </div>
        ) : (
          rateData.map(({ projectId, client, rates, priceField }) => {
            const groups = groupRates(rates, priceField);
            return (
              <div key={projectId} style={{ marginBottom: 32 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: tc.text }}>{projectId}</h3>
                    <span style={{ fontSize: '0.8rem', color: tc.textMuted }}>{client}</span>
                  </div>
                  <button onClick={() => exportCSV(groups, projectId)} style={{
                    ...getButtonStyle('primary', darkMode), display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                    <Download size={14} /> Download CSV
                  </button>
                </div>

                {Object.entries(groups).map(([category, items]) => (
                  <div key={category} style={{ marginBottom: 16 }}>
                    <div style={{
                      borderLeft: `4px solid ${tc.accentPrimary}`,
                      background: tc.topBarBg,
                      padding: '12px 24px', borderRadius: '8px 8px 0 0',
                    }}>
                      <span style={{ fontSize: '0.95rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: tc.text }}>
                        {category}
                      </span>
                    </div>
                    <div style={{
                      background: tc.cardBg, borderRadius: '0 0 12px 12px', overflow: 'hidden',
                      border: `1px solid ${tc.borderLight}`, borderTop: 'none',
                    }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th style={{ padding: '10px 16px', textAlign: 'left', color: tc.textMuted, fontWeight: 600, fontSize: '0.78rem', textTransform: 'uppercase' }}>Code</th>
                            <th style={{ padding: '10px 16px', textAlign: 'left', color: tc.textMuted, fontWeight: 600, fontSize: '0.78rem', textTransform: 'uppercase' }}>Description</th>
                            <th style={{ padding: '10px 16px', textAlign: 'left', color: tc.textMuted, fontWeight: 600, fontSize: '0.78rem', textTransform: 'uppercase' }}>UOM</th>
                            <th style={{ padding: '10px 16px', textAlign: 'right', color: tc.textMuted, fontWeight: 600, fontSize: '0.78rem', textTransform: 'uppercase' }}>Rate</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((item, iIdx) => (
                            <tr key={item.code} style={{
                              borderTop: `1px solid ${tc.borderSubtle}`,
                              backgroundColor: iIdx % 2 === 1 ? (darkMode ? 'rgba(255,255,255,0.02)' : '#f9fafb') : 'transparent',
                            }}>
                              <td style={{ padding: '10px 16px', color: tc.accentPrimary, fontFamily: 'monospace', fontWeight: 600, fontSize: '0.82rem' }}>{item.code}</td>
                              <td style={{ padding: '10px 16px', fontSize: '0.85rem', color: tc.text }}>{item.description}</td>
                              <td style={{ padding: '10px 16px', fontSize: '0.82rem', color: tc.textMuted }}>{item.uom}</td>
                              <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 700, color: tc.text }}>${item.price.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            );
          })
        )}
      </div>
    );
  };

  // Auto-populate prior day from production map status changes
  const populatePriorDay = useCallback(() => {
    let lastSubmitted;
    try { lastSubmitted = localStorage.getItem('streetsheet-last-submitted'); } catch (e) { /* noop */ }
    const cutoff = lastSubmitted || new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

    // Find segments with status activity since last submission
    const recentSegs = mySegments.filter(s => {
      if (s[statusField] === 'Not Started') return false;
      // Check timestamps set by production map
      const phasePrefix = statusField.replace('_status', '');
      if (s[`${phasePrefix}_completed`] && s[`${phasePrefix}_completed`] > cutoff) return true;
      if (s[`${phasePrefix}_started`] && s[`${phasePrefix}_started`] > cutoff) return true;
      // Fallback: updated_at for segments changed outside the map
      if (s.updated_at && s.updated_at > cutoff && (s[statusField] === 'Complete' || s[statusField] === 'In Progress' || s[statusField] === 'QC Approved')) return true;
      return false;
    });

    if (recentSegs.length === 0) return;

    // Group by street
    const byStreet = {};
    recentSegs.forEach(s => {
      const key = s.street || 'Unknown';
      if (!byStreet[key]) byStreet[key] = { footage: 0, segs: [], leaders: new Set(), sections: new Set(), allComplete: true };
      byStreet[key].footage += parseFloat(s.footage) || 0;
      byStreet[key].segs.push(s);
      if (s.crew_name) byStreet[key].leaders.add(s.crew_name);
      if (s.section) byStreet[key].sections.add(s.section);
      if (s[statusField] !== 'Complete' && s[statusField] !== 'QC Approved') byStreet[key].allComplete = false;
    });

    const rows = Object.entries(byStreet).map(([street, data]) => ({
      leader: [...data.leaders][0] || '',
      activity: 'Bore',
      qty: String(Math.round(data.footage)),
      cabinet: [...data.sections][0] || '',
      street,
      completed: data.allComplete,
    }));

    setStreetSheetForm(prev => ({ ...prev, priorDay: rows }));
  }, [mySegments]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-populate prior day when street-sheet tab opens
  useEffect(() => {
    if (activeTab === 'street-sheet' && !priorDayPopulatedRef.current && mySegments.length > 0) {
      priorDayPopulatedRef.current = true;
      populatePriorDay();
    }
    if (activeTab !== 'street-sheet') {
      priorDayPopulatedRef.current = false;
    }
  }, [activeTab, mySegments, populatePriorDay]);

  const renderStreetSheet = () => {
    const updateCrew = (idx, field, value) => {
      const crews = [...streetSheetForm.crews];
      crews[idx] = { ...crews[idx], [field]: value };
      setStreetSheetForm({ ...streetSheetForm, crews });
    };
    const addCrew = () => setStreetSheetForm({ ...streetSheetForm, crews: [...streetSheetForm.crews, { leader: '', street: '', cabinet: '', boreInfo: '' }] });
    const removeCrew = (idx) => {
      if (streetSheetForm.crews.length <= 1) return;
      setStreetSheetForm({ ...streetSheetForm, crews: streetSheetForm.crews.filter((_, i) => i !== idx) });
    };

    const updatePrior = (idx, field, value) => {
      const priorDay = [...streetSheetForm.priorDay];
      priorDay[idx] = { ...priorDay[idx], [field]: value };
      setStreetSheetForm({ ...streetSheetForm, priorDay });
    };
    const addPrior = () => setStreetSheetForm({ ...streetSheetForm, priorDay: [...streetSheetForm.priorDay, { leader: '', activity: 'Bore', qty: '', cabinet: '', street: '', completed: false }] });
    const removePrior = (idx) => {
      if (streetSheetForm.priorDay.length <= 1) return;
      setStreetSheetForm({ ...streetSheetForm, priorDay: streetSheetForm.priorDay.filter((_, i) => i !== idx) });
    };

    // Pre-fill crew leaders from subs
    const crewSuggestions = mySubs.map(s => s.name).filter(Boolean);

    const sectionLabel = (text) => (
      <div style={{
        borderLeft: `4px solid ${tc.accentPrimary}`,
        background: tc.topBarBg,
        padding: '12px 20px', borderRadius: '8px 8px 0 0', marginTop: '24px',
        fontWeight: 700, fontSize: '0.95rem', color: tc.text, textTransform: 'uppercase', letterSpacing: '0.5px',
      }}>
        {text}
      </div>
    );

    return (
      <div>
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '8px', color: tc.text }}>Daily Street Sheet</h2>
          <p style={{ color: tc.textMuted }}>Report crew locations and prior day production (Highline format)</p>
        </div>

        <div style={{ maxWidth: '800px' }}>
          {/* Header */}
          <div style={cardStyle}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: tc.text, fontSize: '0.85rem' }}>Contractor</label>
                <input type="text" value={loggedInUser?.company || loggedInUser?.name || ''} disabled style={{ ...formInputStyle('ss-contractor'), opacity: 0.7 }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: tc.text, fontSize: '0.85rem' }}>County / Project *</label>
                <select value={streetSheetForm.project} onChange={(e) => setStreetSheetForm({ ...streetSheetForm, project: e.target.value })} style={formSelectStyle('ss-project')} {...inputFocusProps('ss-project')}>
                  <option value="">Select Project</option>
                  {projectOptions.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: tc.text, fontSize: '0.85rem' }}>Date</label>
                <input type="date" value={streetSheetForm.date} onChange={(e) => setStreetSheetForm({ ...streetSheetForm, date: e.target.value })} style={formInputStyle('ss-date')} {...inputFocusProps('ss-date')} />
              </div>
            </div>
          </div>

          {/* Section 1: Today's Crews */}
          {sectionLabel("Today's Crews")}
          <div style={{ ...cardStyle, borderRadius: '0 0 12px 12px', marginBottom: 0 }}>
            {streetSheetForm.crews.map((crew, idx) => (
              <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: '12px', marginBottom: '12px', alignItems: 'end' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.78rem', color: tc.textMuted }}>Crew Leader</label>
                  <input type="text" value={crew.leader} onChange={(e) => updateCrew(idx, 'leader', e.target.value)} placeholder={crewSuggestions[idx] || 'Name'} list="crew-leaders" style={formInputStyle(`crew-l-${idx}`)} {...inputFocusProps(`crew-l-${idx}`)} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.78rem', color: tc.textMuted }}>Street Name</label>
                  <input type="text" value={crew.street} onChange={(e) => updateCrew(idx, 'street', e.target.value)} placeholder="Street" style={formInputStyle(`crew-s-${idx}`)} {...inputFocusProps(`crew-s-${idx}`)} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.78rem', color: tc.textMuted }}>Cabinet</label>
                  <input type="text" value={crew.cabinet} onChange={(e) => updateCrew(idx, 'cabinet', e.target.value)} placeholder="Cabinet" style={formInputStyle(`crew-c-${idx}`)} {...inputFocusProps(`crew-c-${idx}`)} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.78rem', color: tc.textMuted }}>Bore/Plow + Seg IDs</label>
                  <input type="text" value={crew.boreInfo} onChange={(e) => updateCrew(idx, 'boreInfo', e.target.value)} placeholder="1 plow/1 drill- 123,456" style={formInputStyle(`crew-b-${idx}`)} {...inputFocusProps(`crew-b-${idx}`)} />
                </div>
                <button onClick={() => removeCrew(idx)} disabled={streetSheetForm.crews.length <= 1} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '10px', color: streetSheetForm.crews.length <= 1 ? tc.textMuted : tc.error, opacity: streetSheetForm.crews.length <= 1 ? 0.3 : 1 }}>
                  <Minus size={18} />
                </button>
              </div>
            ))}
            <button onClick={addCrew} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: `1px dashed ${tc.textMuted}40`, borderRadius: '8px', padding: '10px 16px', color: tc.accentPrimary, cursor: 'pointer', fontWeight: 500, fontSize: '0.85rem', width: '100%', justifyContent: 'center' }}>
              <Plus size={16} /> Add Crew
            </button>
          </div>

          {/* Datalist for crew leader suggestions */}
          <datalist id="crew-leaders">
            {crewSuggestions.map((name, i) => <option key={i} value={name} />)}
          </datalist>

          {/* Section 2: Prior Day Production */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ flex: 1 }}>{sectionLabel("Prior Day Production")}</div>
            <button onClick={populatePriorDay} style={{
              background: 'none', border: `1px solid ${tc.accentPrimary}40`, borderRadius: '8px',
              padding: '8px 14px', color: tc.accentPrimary, cursor: 'pointer', fontWeight: 600,
              fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: '5px', marginTop: '24px',
              whiteSpace: 'nowrap',
            }}>
              <Activity size={14} /> Refresh from Production
            </button>
          </div>
          <div style={{ ...cardStyle, borderRadius: '0 0 12px 12px', marginBottom: 0 }}>
            {streetSheetForm.priorDay.map((row, idx) => (
              <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 0.6fr 0.8fr 1fr auto auto', gap: '12px', marginBottom: '12px', alignItems: 'end' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.78rem', color: tc.textMuted }}>Crew Leader</label>
                  <input type="text" value={row.leader} onChange={(e) => updatePrior(idx, 'leader', e.target.value)} placeholder="Name" list="crew-leaders" style={formInputStyle(`prior-l-${idx}`)} {...inputFocusProps(`prior-l-${idx}`)} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.78rem', color: tc.textMuted }}>Activity</label>
                  <select value={row.activity} onChange={(e) => updatePrior(idx, 'activity', e.target.value)} style={formSelectStyle(`prior-a-${idx}`)} {...inputFocusProps(`prior-a-${idx}`)}>
                    <option value="Bore">Bore</option>
                    <option value="Plow">Plow</option>
                    <option value="Strand">Strand</option>
                    <option value="Fiber">Fiber</option>
                    <option value="Splicing">Splicing</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.78rem', color: tc.textMuted }}>Qty</label>
                  <input type="number" value={row.qty} onChange={(e) => updatePrior(idx, 'qty', e.target.value)} placeholder="0" style={formInputStyle(`prior-q-${idx}`)} {...inputFocusProps(`prior-q-${idx}`)} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.78rem', color: tc.textMuted }}>Cabinet</label>
                  <input type="text" value={row.cabinet} onChange={(e) => updatePrior(idx, 'cabinet', e.target.value)} placeholder="Cabinet" style={formInputStyle(`prior-c-${idx}`)} {...inputFocusProps(`prior-c-${idx}`)} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.78rem', color: tc.textMuted }}>Street</label>
                  <input type="text" value={row.street} onChange={(e) => updatePrior(idx, 'street', e.target.value)} placeholder="Street" style={formInputStyle(`prior-s-${idx}`)} {...inputFocusProps(`prior-s-${idx}`)} />
                </div>
                <div style={{ textAlign: 'center' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.78rem', color: tc.textMuted }}>Done?</label>
                  <button onClick={() => updatePrior(idx, 'completed', !row.completed)} style={{
                    width: '40px', height: '40px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                    backgroundColor: row.completed ? `${tc.accentSecondary}20` : darkMode ? '#374151' : '#f3f4f6',
                    color: row.completed ? tc.accentSecondary : tc.textMuted,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {row.completed ? <CheckCircle size={18} /> : <X size={18} />}
                  </button>
                </div>
                <button onClick={() => removePrior(idx)} disabled={streetSheetForm.priorDay.length <= 1} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '10px', color: streetSheetForm.priorDay.length <= 1 ? tc.textMuted : tc.error, opacity: streetSheetForm.priorDay.length <= 1 ? 0.3 : 1 }}>
                  <Minus size={18} />
                </button>
              </div>
            ))}
            <button onClick={addPrior} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: `1px dashed ${tc.textMuted}40`, borderRadius: '8px', padding: '10px 16px', color: tc.accentPrimary, cursor: 'pointer', fontWeight: 500, fontSize: '0.85rem', width: '100%', justifyContent: 'center' }}>
              <Plus size={16} /> Add Row
            </button>
          </div>

          {/* Submit */}
          <button onClick={handleStreetSheetSubmit} disabled={loading} style={{
            width: '100%', padding: '16px', marginTop: '24px',
            backgroundColor: '#FF9800', color: '#fff', border: 'none', borderRadius: '10px',
            fontSize: '1rem', fontWeight: '700', cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          }}>
            {loading ? <Loader size={18} /> : <Shovel size={18} />}
            {loading ? 'Submitting...' : 'Submit Street Sheet'}
          </button>
        </div>
      </div>
    );
  };

  const renderSettings = () => {
    const selectedBg = darkMode ? 'rgba(102,126,234,0.2)' : 'rgba(0,180,216,0.1)';

    return (
      <div>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: tc.text, marginBottom: '8px' }}>Settings</h2>
        <p style={{ color: tc.textMuted, marginBottom: '24px' }}>Customize your dashboard experience</p>

        <div style={cardStyle}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, color: tc.text, marginBottom: '16px' }}>Appearance</h3>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {[
              { mode: false, Icon: Sun, label: 'Light' },
              { mode: true, Icon: Moon, label: 'Dark' },
            ].map(opt => {
              const isActive = darkMode === opt.mode;
              return (
                <div key={opt.label} onClick={() => setDarkMode(opt.mode)}
                  style={{
                    flex: '1 1 140px', maxWidth: '200px', padding: '24px 20px', borderRadius: '12px', cursor: 'pointer',
                    border: `2px solid ${isActive ? tc.accentPrimary : tc.borderLight}`,
                    background: isActive ? selectedBg : 'transparent',
                    textAlign: 'center', transition: 'all 0.2s',
                  }}>
                  <opt.Icon size={32} color={isActive ? tc.accentPrimary : tc.textMuted} />
                  <div style={{ marginTop: '10px', fontWeight: 600, fontSize: '0.9rem', color: isActive ? tc.accentPrimary : tc.textMuted }}>{opt.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* GPS Toggle */}
        <div style={{ ...cardStyle, marginTop: '24px' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, color: tc.text, marginBottom: '12px' }}>Location</h3>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontWeight: 500, fontSize: '0.9rem', color: tc.text }}>GPS Tracking</div>
              <div style={{ fontSize: '0.8rem', color: tc.textMuted, marginTop: '2px' }}>
                {gpsOn ? 'Location shared for clock-in, photos, and crew tracking' : 'Location disabled — some features may be limited'}
              </div>
            </div>
            <button
              onClick={() => {
                const next = !gpsOn;
                setGpsOn(next);
                setGpsEnabled(next);
              }}
              style={{
                width: '52px', height: '28px', borderRadius: '14px', border: 'none', cursor: 'pointer',
                backgroundColor: gpsOn ? tc.accentPrimary : (darkMode ? '#374151' : '#d1d5db'),
                position: 'relative', transition: 'background-color 0.2s', flexShrink: 0,
              }}
            >
              <div style={{
                width: '22px', height: '22px', borderRadius: '50%', backgroundColor: '#fff',
                position: 'absolute', top: '3px',
                left: gpsOn ? '27px' : '3px',
                transition: 'left 0.2s',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              }} />
            </button>
          </div>
        </div>

        {/* Add Sub-Contractor */}
        <div style={{ ...cardStyle, marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: showAddSub ? '16px' : 0 }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, color: tc.text, margin: 0 }}>Sub-Contractors</h3>
            <button onClick={() => { setShowAddSub(!showAddSub); setAddSubStatus({ show: false, success: false, message: '' }); }}
              style={{
                ...getButtonStyle(showAddSub ? 'outline' : 'primary', darkMode), padding: '6px 14px', fontSize: '0.8rem',
                background: showAddSub ? 'transparent' : tc.gradientPortal,
                display: 'flex', alignItems: 'center', gap: '5px',
              }}>
              {showAddSub ? <X size={13} /> : <Plus size={13} />}
              {showAddSub ? 'Cancel' : 'Add Sub'}
            </button>
          </div>

          {addSubStatus.show && (
            <div style={{
              padding: '12px 16px', borderRadius: '10px', marginBottom: '16px', fontSize: '0.9rem',
              backgroundColor: addSubStatus.success ? (darkMode ? '#4CAF5015' : '#E8F5E9') : (darkMode ? '#f4433615' : '#FFEBEE'),
              border: `1px solid ${addSubStatus.success ? '#4CAF5040' : '#f4433640'}`,
              color: addSubStatus.success ? '#4CAF50' : tc.error,
            }}>
              {addSubStatus.message}
            </div>
          )}

          {showAddSub && (
            <div>
              <div style={{ display: 'grid', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: '0.85rem', color: tc.text }}>Full Name *</label>
                  <input value={newSub.name} onChange={e => setNewSub({ ...newSub, name: e.target.value })}
                    placeholder="John Smith" style={formInputStyle('add-sub-name')} {...inputFocusProps('add-sub-name')} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: '0.85rem', color: tc.text }}>Email *</label>
                  <input type="email" value={newSub.email} onChange={e => setNewSub({ ...newSub, email: e.target.value })}
                    placeholder="john@example.com" style={formInputStyle('add-sub-email')} {...inputFocusProps('add-sub-email')} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: '0.85rem', color: tc.text }}>Company <span style={{ color: tc.textMuted, fontWeight: 400 }}>(optional)</span></label>
                  <input value={newSub.company} onChange={e => setNewSub({ ...newSub, company: e.target.value })}
                    placeholder="Sub Co LLC" style={formInputStyle('add-sub-company')} {...inputFocusProps('add-sub-company')} />
                </div>
              </div>
              <button onClick={handleAddSub} disabled={addSubLoading}
                style={{
                  ...getButtonStyle('primary', darkMode), width: '100%', height: '48px',
                  background: tc.gradientPortal, fontSize: '1rem',
                  cursor: addSubLoading ? 'not-allowed' : 'pointer', opacity: addSubLoading ? 0.7 : 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}>
                {addSubLoading ? <><Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> Adding...</> : <><Plus size={16} /> Add Sub-Contractor</>}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard': return renderDashboard();
      case 'production': return renderProduction();
      case 'equipment': return renderEquipment();
      case 'compliance': return renderCompliance();
      case 'incidents': return renderIncidents();
      case 'assignments': return renderAssignments();
      case 'team': return renderTeam();
      case 'billing': return renderBilling();
      case 'invoices': return renderInvoices();
      case 'rates': return renderRates();
      case 'street-sheet': return renderStreetSheet();
      case 'settings': return renderSettings();
      default: return renderDashboard();
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: tc.bgAlt, display: 'flex', WebkitOverflowScrolling: 'touch', overflowX: 'hidden' }} onClick={(e) => { if (e.detail === 3) setShowVersion(!showVersion); }}>
      {/* Shimmer + pulse animations */}
      <style>{`
        @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
      `}</style>

      {/* Notification Toast */}
      {submitStatus.show && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', left: isMobile ? '20px' : 'auto', padding: '16px 24px', backgroundColor: submitStatus.success ? tc.success : tc.error, color: '#fff', borderRadius: '12px', boxShadow: '0 8px 25px rgba(0,0,0,0.2)', zIndex: 1100, display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.9rem', fontWeight: 500 }}>
          {submitStatus.success ? <CheckCircle size={20} /> : <AlertTriangle size={20} />}
          {submitStatus.message}
        </div>
      )}

      {/* Mobile Header */}
      {isMobile && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: '56px', backgroundColor: tc.cardBg, borderBottom: `1px solid ${tc.borderSubtle}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px', zIndex: 1000 }}>
          <div style={{ fontSize: '1.25rem', fontWeight: '700' }}>
            <span style={{ color: tc.logoLY }}>ly</span>
            <span style={{ color: tc.logoT }}>t</span>
            <span style={{ color: tc.textMuted, fontSize: '0.85rem', marginLeft: '6px' }}>Contractor</span>
          </div>
          <NotificationBell darkMode={darkMode} user={loggedInUser} setCurrentPage={setCurrentPage} />
        </div>
      )}

      {/* Desktop Sidebar — hidden on mobile */}
      {!isMobile && (
        <div style={{ width: '260px', backgroundColor: tc.cardBg, borderRight: `1px solid ${tc.borderSubtle}`, display: 'flex', flexDirection: 'column', position: 'fixed', height: '100vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch', zIndex: 1, top: 0, left: 0 }}>
          {/* Gradient Header */}
          <div style={{ background: tc.gradientPortal, padding: '24px 20px', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', right: '-15px', top: '-15px', width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', position: 'relative', zIndex: 1 }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: '700', fontSize: '1.2rem', flexShrink: 0, border: '2px solid rgba(255,255,255,0.3)' }}>
                {loggedInUser?.name?.charAt(0) || 'C'}
              </div>
              <div>
                <div style={{ fontWeight: '600', color: '#fff', fontSize: '0.95rem' }}>{loggedInUser?.name || 'Contractor'}</div>
                <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)' }}>{loggedInUser?.company || loggedInUser?.email}</div>
              </div>
            </div>
          </div>

          {/* Nav Items */}
          <div style={{ flex: 1, padding: '12px', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
            {navItems.map((item) => {
              const isActive = activeTab === item.id;
              return (
                <button key={item.id} onClick={() => setActiveTab(item.id)} style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: '12px',
                  padding: '14px 16px', marginBottom: '2px',
                  backgroundColor: isActive ? `${tc.accentPrimary}12` : 'transparent',
                  border: 'none', borderRadius: '0 8px 8px 0', cursor: 'pointer',
                  color: isActive ? tc.accentPrimary : tc.text, textAlign: 'left',
                  transition: 'all 0.15s',
                  borderLeft: isActive ? `3px solid ${tc.accentPrimary}` : '3px solid transparent',
                }}>
                  <item.icon size={18} />
                  <span style={{ flex: 1, fontSize: '0.9rem', fontWeight: isActive ? 600 : 400 }}>{item.label}</span>
                  {item.badge > 0 && (
                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 700, background: '#FFB800', color: '#fff' }}>
                      {item.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Bottom: dark mode toggle + sign out */}
          <div style={{ padding: '12px 16px', borderTop: `1px solid ${tc.borderSubtle}` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', padding: '8px 4px' }}>
              <span style={{ fontSize: '0.85rem', color: tc.textMuted }}>Dark Mode</span>
              <button onClick={() => setDarkMode(!darkMode)} style={{
                width: '44px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                background: darkMode ? tc.accentPrimary : tc.border, position: 'relative', transition: 'background 0.2s',
              }}>
                <div style={{
                  width: '20px', height: '20px', borderRadius: '50%', background: '#fff',
                  position: 'absolute', top: '2px', transition: 'left 0.2s',
                  left: darkMode ? '22px' : '2px', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </button>
            </div>
            <button onClick={handleLogout} style={{
              ...getButtonStyle('ghost', darkMode), width: '100%', display: 'flex', alignItems: 'center',
              justifyContent: 'center', gap: '8px', color: tc.error, padding: '10px',
            }}>
              <LogOut size={16} /> Sign Out
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div style={{
        flex: 1, marginLeft: isMobile ? 0 : '260px',
        padding: isMobile ? '72px 16px 88px' : '32px',
        minHeight: '100vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch',
      }}>{renderContent()}</div>

      {/* Mobile Bottom Tab Bar */}
      {isMobile && (
        <>
          <div style={{
            position: 'fixed', bottom: 0, left: 0, right: 0, height: '56px',
            backgroundColor: tc.cardBg, borderTop: `1px solid ${tc.borderSubtle}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-around',
            zIndex: 1000, paddingBottom: 'env(safe-area-inset-bottom)',
          }}>
            {bottomTabs.map(tab => {
              const isActive = tab.id === 'more' ? moreSheetOpen : activeTab === tab.id;
              return (
                <button key={tab.id} onClick={() => {
                  if (tab.id === 'more') { setMoreSheetOpen(!moreSheetOpen); }
                  else { setActiveTab(tab.id); setMoreSheetOpen(false); }
                }} style={{
                  flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px',
                  background: 'none', border: 'none', cursor: 'pointer', padding: '6px 0',
                  color: isActive ? tc.accentPrimary : tc.textMuted, position: 'relative',
                }}>
                  <tab.icon size={22} />
                  <span style={{ fontSize: '0.65rem', fontWeight: isActive ? 700 : 500 }}>{tab.label}</span>
                  {tab.badge > 0 && (
                    <span style={{ position: 'absolute', top: '2px', right: 'calc(50% - 16px)', width: '16px', height: '16px', borderRadius: '50%', background: '#FFB800', color: '#fff', fontSize: '0.6rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* More slide-up sheet */}
          {moreSheetOpen && (
            <>
              <div onClick={() => setMoreSheetOpen(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1001 }} />
              <div style={{
                position: 'fixed', bottom: '56px', left: 0, right: 0, zIndex: 1002,
                backgroundColor: tc.cardBg, borderRadius: '16px 16px 0 0',
                padding: '20px', paddingBottom: '24px',
                boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
              }}>
                <div style={{ width: '40px', height: '4px', borderRadius: '2px', background: tc.border, margin: '0 auto 16px' }} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                  {navItems.filter(n => !['dashboard', 'assignments', 'billing', 'production'].includes(n.id)).map(item => (
                    <button key={item.id} onClick={() => { setActiveTab(item.id); setMoreSheetOpen(false); }} style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                      padding: '16px 8px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                      background: activeTab === item.id ? `${tc.accentPrimary}12` : 'transparent',
                      color: activeTab === item.id ? tc.accentPrimary : tc.text,
                    }}>
                      <item.icon size={22} />
                      <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>{item.label}</span>
                    </button>
                  ))}
                  <button onClick={() => { setDarkMode(!darkMode); }} style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                    padding: '16px 8px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                    background: 'transparent', color: tc.text,
                  }}>
                    {darkMode ? <Sun size={22} /> : <Moon size={22} />}
                    <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>{darkMode ? 'Light' : 'Dark'}</span>
                  </button>
                  <button onClick={() => { setMoreSheetOpen(false); handleLogout(); }} style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                    padding: '16px 8px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                    background: 'transparent', color: tc.error,
                  }}>
                    <LogOut size={22} />
                    <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>Sign Out</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {showVersion && (
        <div style={{ position: 'fixed', bottom: isMobile ? '64px' : '10px', right: '10px', fontSize: '0.7rem', opacity: 0.5, color: tc.text, backgroundColor: tc.cardBg, padding: '4px 8px', borderRadius: '4px', zIndex: 9999 }}>
          ContractorDashboard v3.0
        </div>
      )}
    </div>
  );
};

export default ContractorDashboard;
